import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, MessageSquare, BookOpen, HelpCircle, BookText, FileEdit, Sparkles, Search, ChevronRight, ChevronDown, Copy, CheckCheck, Send, Lightbulb, FlaskConical, FileText, Download, Upload, Eye, EyeOff, AlertTriangle, Check, GripVertical, Trash2, Table2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ResearchAssistant from '@/pages/ResearchAssistant';
import SharedNav from '@/components/SharedNav';

const API = (typeof window !== 'undefined' && window.location && window.location.origin) || process.env.REACT_APP_BACKEND_URL || '';

/* ═══════════════════════════════════════════════════
   STATIC DATA — ZERO AI CALLS
   ═══════════════════════════════════════════════════ */

// ── WIZARD DECISION TREE ──
const WIZARD_STEPS = [
  {
    question: 'What is your primary research objective?',
    options: [
      { label: 'Compare treatments/varieties', next: 1 },
      { label: 'Study trait relationships', next: 2 },
      { label: 'Evaluate breeding/combining ability', next: 3 },
      { label: 'Assess stability across environments', next: 4 },
    ],
  },
  {
    question: 'How is your experiment structured?',
    options: [
      { label: 'No blocking (completely random)', result: { name: 'CRD', desc: 'Completely Randomized Design — use when experimental units are homogeneous.', tab: 'CRD' } },
      { label: 'Blocked by replication', result: { name: 'RBD', desc: 'Randomized Block Design — the most common design in field experiments.', tab: 'RBD' } },
      { label: 'Main plot + sub-plot factors', result: { name: 'Split-Plot', desc: 'Split-Plot Design — for studying two factors at different precision levels.', tab: 'Split-Plot' } },
      { label: 'Many genotypes, incomplete blocks', result: { name: 'Augmented / Alpha Lattice', desc: 'Use Augmented Design for unreplicated entries or Alpha Lattice for large trials.', tab: 'Augmented' } },
    ],
  },
  {
    question: 'What kind of relationship are you studying?',
    options: [
      { label: 'Correlation between two traits', result: { name: 'Correlation Analysis', desc: 'Phenotypic, genotypic, and environmental correlations between traits.', tab: 'Correlation' } },
      { label: 'Cause-effect among multiple traits', result: { name: 'Path Analysis', desc: 'Partitions correlation into direct and indirect effects via path coefficients.', tab: 'Path Analysis' } },
      { label: 'Predict yield from other traits', result: { name: 'Multiple Regression', desc: 'Predict a dependent variable from multiple independent traits.', tab: 'Regression' } },
      { label: 'Group genotypes by similarity', result: { name: 'D² & Cluster Analysis', desc: 'Mahalanobis D² for genetic divergence + Tocher / Ward clustering.', tab: 'D² & Cluster' } },
      { label: 'Reduce dimensions (many traits)', result: { name: 'PCA', desc: 'Principal Component Analysis reduces many correlated traits into key components.', tab: 'PCA' } },
    ],
  },
  {
    question: 'What is your mating/crossing design?',
    options: [
      { label: 'Diallel cross (all parents crossed)', result: { name: 'Diallel Analysis', desc: 'Griffing Methods I-IV for GCA, SCA, and gene action estimation.', tab: 'Diallel' } },
      { label: 'Lines crossed with testers', result: { name: 'Line × Tester', desc: 'Estimates GCA of lines & testers, SCA of crosses, and gene action.', tab: 'Line × Tester' } },
      { label: 'North Carolina Design (males × females)', result: { name: 'NC Design I/II', desc: 'Hierarchical or factorial mating design for variance component estimation.', tab: 'NC Design' } },
      { label: 'Heritability from variance components', result: { name: 'Heritability & Genetic Advance', desc: 'Estimates h² (narrow/broad sense) and genetic advance under selection.', tab: 'Heritability' } },
    ],
  },
  {
    question: 'How many environments are involved?',
    options: [
      { label: '3+ environments, want stability ranking', result: { name: 'Eberhart & Russell Stability', desc: 'Regression-based stability parameters (bi, S²di) for each genotype.', tab: 'Stability' } },
      { label: 'GxE interaction decomposition', result: { name: 'AMMI Analysis', desc: 'Additive Main effects and Multiplicative Interaction model.', tab: 'AMMI' } },
      { label: 'Which-won-where biplot', result: { name: 'GGE Biplot', desc: 'Genotype + GE interaction biplot for mega-environment delineation.', tab: 'GGE Biplot' } },
    ],
  },
];

// ── INTERPRETATION GUIDES (50 entries) ──
const INTERPRETATION_GUIDES = [
  { id: 1, category: 'ANOVA', title: 'F-value significant at p<0.01', interpretation: 'Highly significant treatment effect. At least one treatment mean differs from others. Proceed to mean comparison tests (LSD, DMRT, or Tukey HSD).', action: 'Report as ** in ANOVA table. Calculate CD/LSD for pairwise comparison.' },
  { id: 2, category: 'ANOVA', title: 'F-value significant at p<0.05', interpretation: 'Significant treatment differences exist. Confidence is 95%. Proceed with caution in interpretation.', action: 'Report as * in ANOVA table. Use DMRT for mean separation.' },
  { id: 3, category: 'ANOVA', title: 'F-value non-significant (p>0.05)', interpretation: 'No statistically significant differences among treatments. This does NOT mean treatments are equal — only that the experiment could not detect differences.', action: 'Check experimental error (CV%). If CV>25%, consider increasing replications.' },
  { id: 4, category: 'ANOVA', title: 'High CV% (>20%)', interpretation: 'Coefficient of variation exceeds acceptable limits. This indicates high experimental error or heterogeneous conditions.', action: 'Review field management. Consider using a more precise design (RBD→Lattice) or increasing replications.' },
  { id: 5, category: 'ANOVA', title: 'Low CV% (<5%)', interpretation: 'Very low experimental error. The experiment was conducted with high precision. Results are highly reliable.', action: 'Ideal for publication. Proceed with confidence in your mean comparisons.' },
  { id: 6, category: 'Correlation', title: 'r > +0.7 (strong positive)', interpretation: 'Strong positive linear association. As one trait increases, the other increases proportionally. Useful for indirect selection.', action: 'Confirm with genotypic correlation. If rg > rp, genetic linkage is strong.' },
  { id: 7, category: 'Correlation', title: 'r between +0.3 and +0.7', interpretation: 'Moderate positive association. The relationship exists but other factors also influence the traits.', action: 'Conduct path analysis to determine if the association is direct or indirect through other traits.' },
  { id: 8, category: 'Correlation', title: 'r near 0 (−0.3 to +0.3)', interpretation: 'Weak or no linear association. Traits can be improved independently without affecting each other.', action: 'This is actually useful — independent traits can be selected simultaneously without trade-offs.' },
  { id: 9, category: 'Correlation', title: 'r < −0.7 (strong negative)', interpretation: 'Strong negative association. Improving one trait will significantly reduce the other. Classic example: yield vs. protein content.', action: 'Use selection index to optimize both traits simultaneously. Report the trade-off in discussion.' },
  { id: 10, category: 'Correlation', title: 'rg > rp (genotypic > phenotypic)', interpretation: 'Environmental effects are masking the true genetic association. The genetic linkage is stronger than what phenotype shows.', action: 'Selection based on genotypic correlation will be more effective than phenotypic selection.' },
  { id: 11, category: 'Path Analysis', title: 'High direct effect + high correlation', interpretation: 'This trait has a genuine direct contribution to yield. It is a true cause of yield variation.', action: 'Use this trait as primary selection criterion. Report it as a key yield component.' },
  { id: 12, category: 'Path Analysis', title: 'Low direct effect + high correlation', interpretation: 'The correlation is due to indirect effects through other traits. The trait itself is not a direct yield contributor.', action: 'Do NOT select for this trait directly. Instead, select for the trait causing the indirect effect.' },
  { id: 13, category: 'Path Analysis', title: 'High direct effect + low correlation', interpretation: 'Direct effect is canceled by negative indirect effects through other traits.', action: 'Simultaneous selection (restriction selection index) is needed to capture this direct effect.' },
  { id: 14, category: 'Path Analysis', title: 'Residual effect > 0.5', interpretation: 'More than 50% of yield variation is unexplained by the traits in the model. Important traits are missing.', action: 'Add more yield-contributing traits to the path diagram and re-analyze.' },
  { id: 15, category: 'Diallel', title: 'GCA variance > SCA variance', interpretation: 'Additive gene action predominant. Parents transmit their favorable alleles predictably to progeny.', action: 'Use selection-based breeding: pedigree, bulk, or recurrent selection. Report GCA/SCA ratio.' },
  { id: 16, category: 'Diallel', title: 'SCA variance > GCA variance', interpretation: 'Non-additive (dominance + epistasis) gene action predominant. Specific cross combinations matter more than parent quality.', action: 'Exploit heterosis through hybrid development. F1 hybrids will outperform parents.' },
  { id: 17, category: 'Diallel', title: 'Positive GCA effect', interpretation: 'Parent contributes favorable alleles. Crosses involving this parent will, on average, perform above the population mean.', action: 'Recommend this parent for breeding programs. Use in maximum crosses.' },
  { id: 18, category: 'Diallel', title: 'Negative GCA effect', interpretation: 'Parent contributes unfavorable alleles for this trait. Crosses will underperform on average.', action: 'Avoid this parent for this specific trait. It may still be valuable for other traits.' },
  { id: 19, category: 'Diallel', title: 'High positive SCA effect', interpretation: 'This specific cross performs much better than predicted by the parents GCA. Non-additive gene action is favorable.', action: 'Recommend this cross for F1 hybrid production. Report as best specific combination.' },
  { id: 20, category: 'Diallel', title: 'Degree of dominance = 0', interpretation: 'No dominance. Gene action is purely additive. Each allele contributes independently.', action: 'Selection will be highly effective. Progeny performance is predictable from parents.' },
  { id: 21, category: 'Diallel', title: 'Degree of dominance ≈ 1', interpretation: 'Complete dominance. One allele completely masks the other at each locus.', action: 'Heterosis is expected. Consider hybrid breeding strategies.' },
  { id: 22, category: 'Diallel', title: 'Degree of dominance > 1', interpretation: 'Over-dominance. Heterozygotes are superior to both homozygous parents. Maximum heterosis expected.', action: 'F1 hybrid development is the most promising strategy. Do NOT pursue pure line development.' },
  { id: 23, category: 'Heritability', title: 'h² (broad) > 80%', interpretation: 'High heritability. Most of the observed variation is genetic. Selection will be very effective.', action: 'Simple selection (mass or pedigree) will give good genetic gain. Report as "high heritability."' },
  { id: 24, category: 'Heritability', title: 'h² (broad) 50-80%', interpretation: 'Moderate heritability. Both genetic and environmental factors contribute to variation.', action: 'Progeny testing with replications is recommended for reliable selection.' },
  { id: 25, category: 'Heritability', title: 'h² (broad) < 50%', interpretation: 'Low heritability. Environmental effects dominate. Phenotypic selection will be ineffective.', action: 'Use progeny testing, family selection, or molecular markers (MAS) for genetic improvement.' },
  { id: 26, category: 'Heritability', title: 'High h² + High genetic advance', interpretation: 'Additive gene action controls this trait. Direct selection will be very rewarding.', action: 'This is the ideal scenario for breeding. Prioritize this trait for selection.' },
  { id: 27, category: 'Heritability', title: 'High h² + Low genetic advance', interpretation: 'Non-additive gene action (dominance/epistasis) inflates heritability. Selection will NOT give expected gains.', action: 'Hybrid breeding or heterosis exploitation is more appropriate than pure line selection.' },
  { id: 28, category: 'Stability', title: 'bi ≈ 1, S²di ≈ 0', interpretation: 'Average stability with predictable performance. The genotype adapts proportionally to environmental changes.', action: 'Recommend for general cultivation across a wide range of environments.' },
  { id: 29, category: 'Stability', title: 'bi > 1, S²di ≈ 0', interpretation: 'Adapted to favorable environments. Above-average response to good conditions but performs poorly under stress.', action: 'Recommend only for high-input or irrigated conditions. Not for marginal areas.' },
  { id: 30, category: 'Stability', title: 'bi < 1, S²di ≈ 0', interpretation: 'Adapted to unfavorable environments. Performs relatively better under stress than in favorable conditions.', action: 'Recommend for rainfed, low-input, or stress-prone areas.' },
  { id: 31, category: 'Stability', title: 'High S²di (deviation from regression)', interpretation: 'Unpredictable performance. The genotype behaves inconsistently across environments.', action: 'Do NOT recommend for general release. May be useful for specific niche environments.' },
  { id: 32, category: 'PCA', title: 'First 2 PCs explain >70% variance', interpretation: 'The first two principal components capture most of the variation. A 2D biplot will represent the data well.', action: 'Use the PC1-PC2 biplot for visual interpretation. Report cumulative variance explained.' },
  { id: 33, category: 'PCA', title: 'Trait loading > 0.7 on a PC', interpretation: 'This trait strongly contributes to this principal component. It is a key trait defining that axis of variation.', action: 'Name the PC after its dominant traits (e.g., "Yield Component PC" or "Quality PC").' },
  { id: 34, category: 'PCA', title: 'Eigenvalue > 1 (Kaiser criterion)', interpretation: 'This PC explains more variance than a single original variable. It is meaningful and should be retained.', action: 'Retain all PCs with eigenvalue > 1. Discard the rest.' },
  { id: 35, category: 'Cluster', title: 'Maximum inter-cluster distance', interpretation: 'These two clusters are most genetically diverse. Crosses between members of these clusters will maximize heterosis.', action: 'Select one parent from each of these two clusters for hybridization programs.' },
  { id: 36, category: 'Cluster', title: 'Minimum intra-cluster distance', interpretation: 'Genotypes within this cluster are very similar to each other genetically.', action: 'Avoid crossing genotypes from the same cluster — limited genetic gain expected.' },
  { id: 37, category: 'Cluster', title: 'Single genotype in a cluster', interpretation: 'This genotype is genetically unique and distant from all others. It may carry rare alleles.', action: 'This genotype is valuable for broadening the genetic base. Use as a diverse parent.' },
  { id: 38, category: 'D²', title: 'D² values significant', interpretation: 'Statistically significant genetic distance between clusters. The divergence is real, not due to chance.', action: 'Report in Tocher grouping table. Use inter-cluster D² for parent selection.' },
  { id: 39, category: 'Line×Tester', title: 'Line GCA significant', interpretation: 'Lines differ significantly in their general combining ability. Additive genetic variation among lines exists.', action: 'Select lines with highest positive GCA for the trait of interest.' },
  { id: 40, category: 'Line×Tester', title: 'SCA variance > GCA variance', interpretation: 'Specific cross combinations are more important than individual parent quality. Non-additive gene action prevails.', action: 'Identify best specific crosses for hybrid development rather than relying on parent GCA alone.' },
  { id: 41, category: 'AMMI', title: 'IPCA1 explains >50% of GxE', interpretation: 'The first interaction principal component captures the dominant GxE pattern. The AMMI1 biplot is informative.', action: 'Use AMMI1 biplot for mega-environment identification. Genotypes near zero are stable.' },
  { id: 42, category: 'AMMI', title: 'ASV (AMMI Stability Value) near 0', interpretation: 'This genotype has minimal genotype × environment interaction. Stable across all tested environments.', action: 'Recommend for broad adaptation. Include in varietal release proposals.' },
  { id: 43, category: 'GGE Biplot', title: 'Which-won-where pattern clear', interpretation: 'Different genotypes win in different mega-environments. Targeted selection is possible.', action: 'Identify mega-environments and recommend winning genotype for each zone.' },
  { id: 44, category: 'GGE Biplot', title: 'Genotype near "ideal genotype" position', interpretation: 'This genotype has both high mean performance and high stability — the perfect combination.', action: 'Recommend as the best overall genotype for variety release.' },
  { id: 45, category: 'Selection Index', title: 'High selection differential', interpretation: 'Large difference between the selected population mean and the original population mean.', action: 'Indicates effective selection. Calculate genetic gain using the heritability of each trait.' },
  { id: 46, category: 'Regression', title: 'R² > 0.75', interpretation: 'The model explains more than 75% of the variation in the dependent variable. Strong predictive power.', action: 'The model is suitable for prediction. Report the regression equation prominently.' },
  { id: 47, category: 'Regression', title: 'R² < 0.30', interpretation: 'Poor model fit. Most variation is unexplained. The independent variables are weak predictors.', action: 'Add more predictor variables, transform data, or try non-linear regression.' },
  { id: 48, category: 'Experimental', title: 'Significant Block effect in RBD', interpretation: 'Blocking was effective in reducing experimental error. The experimental design choice was correct.', action: 'Report that blocking accounted for significant field variation, improving treatment comparisons.' },
  { id: 49, category: 'Experimental', title: 'Non-significant Block effect in RBD', interpretation: 'Blocking did not reduce error. The field was more homogeneous than expected.', action: 'CRD might have been sufficient, but RBD did not harm the analysis. Continue normally.' },
  { id: 50, category: 'Molecular', title: 'Marker-trait association p<0.001', interpretation: 'Highly significant association between molecular marker and phenotypic trait. Potential QTL detected.', action: 'Validate in independent population. Report marker position, R², and allelic effects.' },
  // ── Heterosis & Generation Mean references — Niranjan, Feb 2026 (Phase B) ──
  { id: 51, category: 'Heterosis', title: 'Mid-Parent Heterosis (MPH) significant', interpretation: 'F1 mean significantly exceeds the mid-parent value: MPH (%) = [(F1̄ − MP̄)/MP̄] × 100. Indicates dominance or over-dominance gene action. Original formulation by Hayes & Immer (1942) and Fonseca & Patterson (1968), Crop Sci 8:85-88.', action: 'Report the cross with the highest MPH for hybrid breeding. Use Wynne, Emery & Rice (1970) t-test SE = √(3MSE/2r) to flag significance with * (p<0.05) and ** (p<0.01).' },
  { id: 52, category: 'Heterosis', title: 'Better-Parent Heterosis (BPH / Heterobeltiosis)', interpretation: 'F1 mean exceeds the better parent: BPH (%) = [(F1̄ − BP̄)/BP̄] × 100. Term "heterobeltiosis" coined by Bitzer, Patterson & Bridges (1972) Crop Sci 12:268. Formula from Fonseca & Patterson (1968).', action: 'Use as the primary commercial criterion — only crosses with significant positive BPH justify commercial F1 hybrid release. SE = √(2MSE/r).' },
  { id: 53, category: 'Heterosis', title: 'Standard Heterosis (SH / Economic Heterosis)', interpretation: 'F1 outperforms the regional check variety: SH (%) = [(F1̄ − ŜC)/ŜC] × 100. Coined by Meredith & Bridge (1972) Crop Sci 12:304-310. The most relevant heterosis estimator for variety release decisions.', action: 'Always include the locally-recommended check variety as a control plot. Crosses with positive significant SH are the candidates for varietal trial promotion.' },
  { id: 54, category: 'Heterosis', title: 'Combined selection: high mean + high GCA + high SCA + high heterosis', interpretation: 'A cross that ranks in the top fraction on all four criteria is genuinely superior — not an artefact of a single estimator. Singh & Chaudhary (1985) Biometrical Methods, Kalyani, Ch. 4 emphasises this multi-criterion approach for hybrid selection.', action: 'Use the BKAgriD Combined Selection Table (M/G/S/h/b/s pill flags). Crosses scoring 5/6 or 6/6 are strong candidates for advancement.' },
  { id: 55, category: 'GenerationMean', title: "Cavalli's joint scaling test: chi-square non-significant (p > 0.05)", interpretation: '3-parameter additive-dominance model (m, [d], [h]) is adequate. No evidence of digenic epistasis. Cavalli (1952) in: Quantitative Inheritance, HMSO London. WLS solution: β̂ = (X′WX)⁻¹ X′WY.', action: 'Use simple pedigree or single-seed-descent selection. Predict F2 and BC means from the 3-param estimates. Trait gains will be approximately additive.' },
  { id: 56, category: 'GenerationMean', title: "Cavalli's chi-square significant (p ≤ 0.05) — fit Hayman 6-param model", interpretation: 'The 3-param model is rejected — digenic epistasis ([i], [j], [l]) is operating. Hayman (1958) Heredity 12:371-390 introduced the 6-parameter model with [i] additive×additive, [j] additive×dominance, [l] dominance×dominance interactions.', action: 'Refit using the Hayman 6-parameter model. Identify which epistatic component dominates and adjust breeding strategy: pure-line selection becomes unreliable in early generations when [i] or [l] is large.' },
  { id: 57, category: 'GenerationMean', title: 'Mather-Jinks A/B/C/D scaling tests significant', interpretation: 'A = 2BC1̄ − P1̄ − F1̄, B = 2BC2̄ − P2̄ − F1̄, C = 4F2̄ − 2F1̄ − P1̄ − P2̄, D = 2F2̄ − BC1̄ − BC2̄ (Mather 1949; Mather & Jinks 1971). Any of A, B, C, D significantly different from zero indicates non-allelic gene interaction.', action: 'Report all four tests with t-statistic, p-value, and significance. Confirms or refutes the Cavalli verdict — if all four are non-significant the additive-dominance model is robustly supported.' },
  { id: 58, category: 'GenerationMean', title: 'Degree of dominance |h/d|: classification', interpretation: '|h/d| < 0.2 → predominantly additive; 0.2-1.0 → partial dominance; 1.0-2.0 → over-dominance; > 2.0 → strong over-dominance / pseudo-overdominance. Singh & Chaudhary (1985), Singh & Singh (1992) Indian J. Genet. 52:369-375.', action: 'Choose breeding strategy from the verdict: <0.2 → pedigree/SSD; 0.2-1 → modified pedigree with later evaluation; >1 → hybrid breeding (heterotic pools); >2 → strong hybrid focus, develop maintainer and restorer lines.' },
];

const GUIDE_CATEGORIES = [...new Set(INTERPRETATION_GUIDES.map(g => g.category))];

// ── STATISTICAL GLOSSARY (100 terms) ──
const GLOSSARY = [
  { term: 'ANOVA', definition: 'Analysis of Variance. Partitions total variation into treatment, block, and error components to test mean differences.' },
  { term: 'Additive gene action', definition: 'Each allele contributes a fixed amount to the phenotype. No dominance. h² narrow-sense is relevant.' },
  { term: 'AMMI', definition: 'Additive Main effects and Multiplicative Interaction model. Combines ANOVA for main effects with PCA for GxE interaction.' },
  { term: 'Augmented design', definition: 'Experimental design for evaluating large numbers of unreplicated entries using replicated checks for error estimation.' },
  { term: 'Baker ratio', definition: 'Ratio 2σ²GCA/(2σ²GCA+σ²SCA). Values near 1 indicate predominantly additive gene action.' },
  { term: 'Bi (regression coefficient)', definition: 'Eberhart-Russell stability parameter. bi=1 means average stability, bi>1 adapted to good environments, bi<1 adapted to poor.' },
  { term: 'Biplot', definition: 'Two-dimensional graphical display showing relationships among genotypes, environments, or traits using principal components.' },
  { term: 'Block', definition: 'Group of homogeneous experimental units in which all treatments are tested, reducing error variance.' },
  { term: 'Broad-sense heritability (H²)', definition: 'Proportion of phenotypic variance due to total genetic variance: H²=Vg/Vp. Includes additive + dominance + epistasis.' },
  { term: 'CD (Critical Difference)', definition: 'Minimum difference between two means required for statistical significance. CD=t×SE(diff).' },
  { term: 'Cluster analysis', definition: 'Groups genotypes into clusters based on genetic distance (D²). Methods: Tocher, Ward, UPGMA, K-means.' },
  { term: 'Coefficient of Variation (CV%)', definition: 'CV = (√MSE/Grand Mean)×100. Measures experimental precision. <10% excellent, 10-20% good, >25% poor.' },
  { term: 'Combining ability', definition: 'Capacity of a parent to combine well in crosses. GCA = general, SCA = specific cross combinations.' },
  { term: 'Complete dominance', definition: 'Heterozygote phenotype equals one homozygous parent. Degree of dominance = 1.' },
  { term: 'Correlation coefficient (r)', definition: 'Measures strength and direction of linear association between two variables. Range: -1 to +1.' },
  { term: 'CRD', definition: 'Completely Randomized Design. Simplest design; treatments randomly assigned to all units. Best for homogeneous conditions.' },
  { term: 'D² (Mahalanobis distance)', definition: 'Multivariate genetic distance measure between populations. Accounts for correlations between traits.' },
  { term: 'Degrees of freedom (df)', definition: 'Number of independent observations minus constraints. df=n-1 for a sample variance.' },
  { term: 'DFA', definition: 'Discriminant Function Analysis. Classifies observations into predefined groups based on multiple variables.' },
  { term: 'Diallel cross', definition: 'Mating design crossing all parents in all combinations. Griffing: 4 Methods × 2 Models = 8 analyses.' },
  { term: 'Direct effect', definition: 'Path coefficient measuring the direct influence of one trait on another, independent of indirect paths.' },
  { term: 'DMRT', definition: 'Duncan Multiple Range Test. Post-hoc test for pairwise mean comparisons after significant ANOVA F-test.' },
  { term: 'Dominance variance (Vd)', definition: 'Portion of genetic variance due to dominance (interaction between alleles at same locus). Vd=σ²SCA.' },
  { term: 'Eberhart-Russell model', definition: 'Stability analysis: Yi=μ+biIj+δij. Parameters: bi (regression on environmental index), S²di (deviation variance).' },
  { term: 'Eigenvalue', definition: 'Amount of variance explained by a principal component. Retain PCs with eigenvalue > 1 (Kaiser criterion).' },
  { term: 'Environment index', definition: 'Deviation of environmental mean from grand mean. Ij=Mean_j - Grand Mean.' },
  { term: 'Epistasis', definition: 'Interaction between genes at different loci. Creates non-linear gene action not captured by additive or dominance models.' },
  { term: 'Error Mean Square (MSE)', definition: 'Estimate of experimental error variance. Used as denominator in F-tests and for calculating SE and CD.' },
  { term: 'F-test', definition: 'Ratio of treatment MS to error MS. Tests if treatment means differ significantly.' },
  { term: 'F1 hybrid', definition: 'First filial generation from crossing two parents. Exhibits heterosis if dominance/overdominance present.' },
  { term: 'GCA (General Combining Ability)', definition: 'Average performance of a parent across all crosses. GCA reflects additive gene action.' },
  { term: 'Genetic advance (GA)', definition: 'Expected genetic gain from one cycle of selection. GA = k × σp × h². GA as % of mean indicates selection efficiency.' },
  { term: 'Genetic divergence', definition: 'Degree of genetic difference between populations. Measured by D², cluster distance, or FST.' },
  { term: 'Genotype', definition: 'The genetic constitution of an organism. In field trials, refers to a variety/line/accession being tested.' },
  { term: 'Genotypic correlation', definition: 'Correlation between genetic components of two traits. Often higher than phenotypic correlation.' },
  { term: 'GGE Biplot', definition: 'Genotype + Genotype×Environment biplot. Visualizes which-won-where, mean vs stability, discriminativeness of environments.' },
  { term: 'GxE interaction', definition: 'Differential genotype response across environments. Makes variety recommendation environment-specific.' },
  { term: 'Griffing methods', definition: 'Method I: parents+F1s+reciprocals. II: parents+F1s. III: F1s+reciprocals. IV: F1s only.' },
  { term: 'h² narrow-sense', definition: 'Proportion of phenotypic variance due to additive genetic variance only: h²=Va/Vp. Predicts selection response.' },
  { term: 'Hayman analysis', definition: 'Vr-Wr graph method for diallel data. Tests adequacy of additive-dominance model and estimates degree of dominance.' },
  { term: 'Heterosis', definition: 'Superiority of F1 hybrid over parents. Mid-parent heterosis=(F1-MP)/MP×100. Heterobeltiosis uses better parent.' },
  { term: 'Indirect effect', definition: 'Path coefficient measuring influence of trait X on yield through trait Z. Product of rXZ×direct_effect_Z.' },
  { term: 'IPCA', definition: 'Interaction Principal Component Axis. Captures GxE interaction pattern in AMMI analysis.' },
  { term: 'K-means clustering', definition: 'Non-hierarchical clustering that partitions n observations into k clusters minimizing within-cluster variance.' },
  { term: 'Kurtosis', definition: 'Measure of tail heaviness. >3 means heavy tails (leptokurtic), <3 means light tails (platykurtic).' },
  { term: 'Lattice design', definition: 'Incomplete block design for large genotype trials. Each block contains a subset of treatments.' },
  { term: 'Line × Tester', definition: 'Mating design crossing m lines with n testers. Estimates GCA of both, SCA of crosses, proportional contribution.' },
  { term: 'LSD (Least Significant Difference)', definition: 'Minimum difference between two treatment means for significance at chosen alpha level. LSD=t×√(2MSE/r).' },
  { term: 'Mean square (MS)', definition: 'Sum of squares divided by degrees of freedom. Treatment MS tests hypotheses; Error MS estimates variance.' },
  { term: 'Mega-environment', definition: 'Group of environments where the same genotypes consistently win. Identified via GGE biplot analysis.' },
  { term: 'Multiple regression', definition: 'Regression with multiple independent variables: Y=β₀+β₁X₁+β₂X₂+...+ε. R² measures overall fit.' },
  { term: 'Narrow-sense heritability', definition: 'h²=Va/Vp. Only additive variance considered. Best predictor of selection response in breeding.' },
  { term: 'NC Design I', definition: 'North Carolina Design I (hierarchical). Each male mated with several females (nested). Estimates Va and Vd.' },
  { term: 'NC Design II', definition: 'North Carolina Design II (factorial). Every male crossed with every female. Like a mini-diallel between two groups.' },
  { term: 'Non-parametric test', definition: 'Statistical test not assuming normal distribution. Examples: Mann-Whitney, Kruskal-Wallis, Friedman.' },
  { term: 'Over-dominance', definition: 'Heterozygote is superior to both homozygous parents. Degree of dominance > 1. Maximum heterosis.' },
  { term: 'p-value', definition: 'Probability of obtaining results as extreme as observed, assuming null hypothesis is true. p<0.05 = significant.' },
  { term: 'Partial dominance', definition: 'Heterozygote phenotype between two homozygous parents but closer to dominant parent. DoD between 0 and 1.' },
  { term: 'Path analysis', definition: 'Extension of multiple regression. Decomposes correlation between traits into direct and indirect effects.' },
  { term: 'PCA', definition: 'Principal Component Analysis. Reduces dimensionality by creating orthogonal linear combinations of original variables.' },
  { term: 'Phenotypic variance (Vp)', definition: 'Total observed variance. Vp = Vg + Ve (genetic + environmental). Partitioned by ANOVA.' },
  { term: 'QTL', definition: 'Quantitative Trait Locus. Chromosomal region associated with variation in a quantitative trait.' },
  { term: 'R² (coefficient of determination)', definition: 'Proportion of variance in dependent variable explained by independent variables. R²=SSreg/SStotal.' },
  { term: 'RBD', definition: 'Randomized Block Design. Each block contains all treatments. Removes block-to-block variation from error.' },
  { term: 'Reciprocal cross', definition: 'Cross where male and female roles are reversed (A×B and B×A). Tests maternal/cytoplasmic effects.' },
  { term: 'Regression', definition: 'Models the relationship between dependent and independent variables. Y=a+bX for simple linear regression.' },
  { term: 'Replication', definition: 'Repetition of treatments in an experiment. Provides degrees of freedom for error estimation and improves precision.' },
  { term: 'Residual effect', definition: 'In path analysis, the unexplained portion: R=√(1-R²). Values >0.5 indicate missing important variables.' },
  { term: 'S²di', definition: 'Eberhart-Russell deviation from regression. S²di≈0 means predictable; S²di>0 means unpredictable performance.' },
  { term: 'SCA (Specific Combining Ability)', definition: 'Deviation of a cross from what is expected based on GCA of its parents. Reflects non-additive gene action.' },
  { term: 'Scree plot', definition: 'Graph of eigenvalues vs. PC number. The "elbow" indicates the optimal number of components to retain.' },
  { term: 'SE (Standard Error)', definition: 'Standard deviation of a statistic sampling distribution. SE of mean = σ/√n. Used to calculate CD.' },
  { term: 'Selection index', definition: 'Linear combination of traits weighted by economic values and genetic parameters. I=b₁x₁+b₂x₂+...+bₙxₙ.' },
  { term: 'Selection intensity (k)', definition: 'Standardized selection differential. k=2.06 for top 5%, k=1.76 for top 10%, k=1.40 for top 20%.' },
  { term: 'Skewness', definition: 'Measure of distribution asymmetry. 0=symmetric, >0=right-skewed (long right tail), <0=left-skewed.' },
  { term: 'Split-plot design', definition: 'Main factor in whole plots, sub-factor in sub-plots. Two error terms: main-plot error and sub-plot error.' },
  { term: 'Stability analysis', definition: 'Methods to assess genotype consistency across environments: Eberhart-Russell, AMMI, GGE, Finlay-Wilkinson.' },
  { term: 'Standard deviation (σ)', definition: 'Square root of variance. Measures spread of observations around the mean in original units.' },
  { term: 'Strip-plot design', definition: 'Two-factor design with one factor in horizontal strips and another in vertical strips. Three error terms.' },
  { term: 'Sum of squares (SS)', definition: 'Total squared deviations from mean. Partitioned in ANOVA: SStotal = SStreatment + SSblock + SSerror.' },
  { term: 't-test', definition: 'Tests if two means differ significantly. Paired or unpaired. t = (mean_diff)/(SE_diff). Compare to t-table.' },
  { term: 'Tocher method', definition: 'Non-hierarchical clustering for D² grouping. Starts with closest pair, adds genotypes maintaining cluster criterion.' },
  { term: 'Treatment', definition: 'Factor level applied to experimental units: variety, fertilizer dose, spacing, etc.' },
  { term: 'Triple Test Cross', definition: 'Design to detect epistasis. Each line crossed with P1, P2, and F1. Tests adequacy of additive-dominance model.' },
  { term: 'Tukey HSD', definition: 'Honestly Significant Difference test. Controls family-wise error rate for all pairwise comparisons.' },
  { term: 'Type I error (α)', definition: 'Rejecting a true null hypothesis (false positive). α=0.05 means 5% risk of false detection.' },
  { term: 'Type II error (β)', definition: 'Failing to reject a false null hypothesis (false negative). Power=1-β is the probability of detecting a real effect.' },
  { term: 'UPGMA', definition: 'Unweighted Pair-Group Method with Arithmetic Mean. Hierarchical clustering producing dendrograms.' },
  { term: 'Variance (σ²)', definition: 'Average squared deviation from mean. Variance = SS/(n-1). Measures dispersion of data.' },
  { term: 'Variance components', definition: 'Estimated portions of total variance attributed to different sources: σ²g, σ²e, σ²gca, σ²sca.' },
  { term: 'Vr (array variance)', definition: 'Hayman: Variance of r-th array (row) in diallel table. Used in Vr-Wr regression graph.' },
  { term: 'Ward\'s method', definition: 'Hierarchical clustering minimizing within-cluster variance at each merging step. Creates compact spherical clusters.' },
  { term: 'Wr (array covariance)', definition: 'Hayman: Covariance of r-th array with parental diagonal in diallel table. Wr regressed on Vr tests dominance.' },
  { term: 'Yield stability', definition: 'Consistency of genotype performance across environments. Stable genotype: bi≈1, S²di≈0, low ASV, near origin on biplot.' },
  { term: 'σ²GCA', definition: 'Variance due to General Combining Ability. Measures additive genetic variance component in combining ability analysis.' },
  { term: 'σ²SCA', definition: 'Variance due to Specific Combining Ability. Measures non-additive genetic variance in combining ability analysis.' },
  { term: 'χ² test', definition: 'Chi-square test for goodness-of-fit or independence. Compares observed vs. expected frequencies.' },
  { term: 'GWAS', definition: 'Genome-Wide Association Study. Scans entire genome for markers associated with phenotypic variation.' },
  { term: 'MAS', definition: 'Marker-Assisted Selection. Uses DNA markers linked to QTLs for indirect selection of desired traits.' },
  { term: 'Genomic selection', definition: 'Uses genome-wide markers to predict breeding values without identifying specific QTLs. GEBV estimation.' },
  { term: 'BLUE', definition: 'Best Linear Unbiased Estimation. Mixed model approach for estimating fixed effects (treatment means).' },
  { term: 'BLUP', definition: 'Best Linear Unbiased Prediction. Predicts random effects (breeding values) in mixed models.' },
];

// ── RESEARCH INSPIRATION (pre-loaded weekly content) ──
const WEEKLY_TOPICS = [
  { week: 'Wheat Yield Improvement', ideas: ['Evaluate GCA/SCA for grain filling duration using diallel analysis', 'Path analysis: thousand-grain weight → yield via canopy architecture', 'AMMI stability of heat-tolerant lines across NWPZ zones'] },
  { week: 'Rice Quality Breeding', ideas: ['Correlation of amylose content with cooking quality in Basmati landraces', 'QTL mapping for grain elongation ratio using RIL population', 'Selection index combining yield, grain length, and aroma'] },
  { week: 'Pulse Crop Improvement', ideas: ['Genetic divergence (D²) in chickpea germplasm for drought traits', 'Line × Tester analysis for protein content in mungbean', 'Stability of short-duration pigeonpea across semi-arid environments'] },
  { week: 'Oilseed Crop Research', ideas: ['Heterosis estimation in sunflower hybrids for oil content', 'GGE biplot for mustard yield across irrigated vs rainfed', 'NC Design II for fatty acid composition in soybean'] },
  { week: 'Vegetable Breeding', ideas: ['Diallel analysis for fruit weight and shelf life in tomato', 'Path analysis: fruit number × fruit weight → total yield in chilli', 'Augmented design for screening 200 okra accessions'] },
  { week: 'Millets & Climate Resilience', ideas: ['Stability analysis of pearl millet hybrids under terminal drought', 'Cluster analysis of finger millet landraces for nutritional traits', 'Heritability of stay-green trait in sorghum under post-flowering stress'] },
  { week: 'Cotton Fiber Quality', ideas: ['Combining ability for fiber length, strength, and micronaire', 'GxE interaction for lint yield using AMMI analysis', 'Molecular markers associated with fiber fineness QTLs'] },
  { week: 'Sugarcane & Tropical Crops', ideas: ['RBD factorial for nitrogen × variety interaction on sugar recovery', 'PCA of morphological diversity in sugarcane clones', 'Selection index for cane yield, sucrose, and ratoon performance'] },
];


/* ═══════════════════════════════════════════════════
   TAB COMPONENTS
   ═══════════════════════════════════════════════════ */

function ChooseAnalysisWizard() {
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const handleOption = (opt) => {
    if (opt.result) {
      setResult(opt.result);
    } else if (opt.next !== undefined) {
      setHistory([...history, step]);
      setStep(opt.next);
    }
  };

  const goBack = () => {
    if (history.length > 0) {
      setStep(history[history.length - 1]);
      setHistory(history.slice(0, -1));
      setResult(null);
    }
  };

  const reset = () => { setStep(0); setResult(null); setHistory([]); };

  if (result) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center" data-testid="wizard-result">
        <div className="w-16 h-16 rounded-2xl bg-[#2D8B57]/10 flex items-center justify-center mx-auto mb-4">
          <FlaskConical size={28} className="text-[#2D8B57]" />
        </div>
        <h3 className="text-xl font-bold text-[#1F4E2E] mb-2">{result.name}</h3>
        <p className="text-sm text-[#555] mb-6 max-w-md mx-auto">{result.desc}</p>
        <button onClick={reset} className="px-5 py-2 bg-[#2D8B57] text-white rounded-lg text-sm font-medium hover:bg-[#246E47] transition" data-testid="wizard-restart">Start Over</button>
      </div>
    );
  }

  const current = WIZARD_STEPS[step];
  return (
    <div className="max-w-xl mx-auto py-10" data-testid="wizard-step">
      <div className="flex items-center gap-2 mb-6">
        {history.length > 0 && <button onClick={goBack} className="text-[#2D8B57] hover:text-[#1F4E2E] text-sm font-medium flex items-center gap-1"><ArrowLeft size={14} /> Back</button>}
        <span className="text-[11px] text-[#888] ml-auto">Step {history.length + 1} of 3</span>
      </div>
      <h3 className="text-lg font-bold text-[#1F4E2E] mb-5">{current.question}</h3>
      <div className="space-y-2.5">
        {current.options.map((opt, i) => (
          <button key={i} onClick={() => handleOption(opt)}
            className="w-full text-left px-4 py-3 bg-white border border-[#E0EDE0] rounded-xl hover:border-[#2D8B57] hover:bg-[#F4FAF5] transition flex items-center justify-between group"
            data-testid={`wizard-opt-${i}`}>
            <span className="text-sm text-[#333]">{opt.label}</span>
            <ChevronRight size={16} className="text-[#aaa] group-hover:text-[#2D8B57] transition" />
          </button>
        ))}
      </div>
    </div>
  );
}


function InterpretationGuides() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedId, setExpandedId] = useState(null);

  const filtered = INTERPRETATION_GUIDES.filter(g => {
    const matchesCat = activeCategory === 'All' || g.category === activeCategory;
    const matchesSearch = !search || g.title.toLowerCase().includes(search.toLowerCase()) || g.interpretation.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="py-4" data-testid="interp-guides">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search guides..." className="w-full pl-9 pr-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="guide-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['All', ...GUIDE_CATEGORIES].map(c => (
            <button key={c} onClick={() => setActiveCategory(c)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${activeCategory === c ? 'bg-[#2D8B57] text-white' : 'bg-white border border-[#E0EDE0] text-[#555] hover:border-[#2D8B57]'}`}>{c}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-[#888] mb-3">{filtered.length} guides found</p>
      <div className="space-y-2">
        {filtered.map(g => (
          <div key={g.id} className="border border-[#E0EDE0] rounded-xl overflow-hidden bg-white" data-testid={`guide-${g.id}`}>
            <button onClick={() => setExpandedId(expandedId === g.id ? null : g.id)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#F7FBF7] transition">
              <span className="text-[10px] font-bold bg-[#2D8B57]/10 text-[#2D8B57] px-2 py-0.5 rounded shrink-0">{g.category}</span>
              <span className="text-sm font-medium text-[#333] flex-1">{g.title}</span>
              <ChevronDown size={14} className={`text-[#999] transition-transform ${expandedId === g.id ? 'rotate-180' : ''}`} />
            </button>
            {expandedId === g.id && (
              <div className="px-4 pb-3 border-t border-[#F0F0F0]">
                <p className="text-sm text-[#333] mt-2 mb-2"><strong>Interpretation:</strong> {g.interpretation}</p>
                <p className="text-sm text-[#2D8B57]"><strong>Action:</strong> {g.action}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function StatisticalGlossary() {
  const [search, setSearch] = useState('');
  const filtered = GLOSSARY.filter(g => !search || g.term.toLowerCase().includes(search.toLowerCase()) || g.definition.toLowerCase().includes(search.toLowerCase()));
  const alphabet = [...new Set(GLOSSARY.map(g => g.term[0].toUpperCase()))].sort();

  return (
    <div className="py-4" data-testid="glossary">
      <div className="relative max-w-lg mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search 100 terms..." className="w-full pl-9 pr-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="glossary-search" />
      </div>
      <div className="flex gap-1 flex-wrap mb-4">
        {alphabet.map(l => (
          <button key={l} onClick={() => setSearch(l)} className="w-7 h-7 rounded text-[10px] font-bold bg-white border border-[#E0EDE0] hover:bg-[#2D8B57] hover:text-white transition">{l}</button>
        ))}
        {search && <button onClick={() => setSearch('')} className="px-2 h-7 rounded text-[10px] font-medium bg-red-50 text-red-600 border border-red-200">Clear</button>}
      </div>
      <p className="text-[11px] text-[#888] mb-3">{filtered.length} terms</p>
      <div className="grid gap-2">
        {filtered.map(g => (
          <div key={g.term} className="bg-white border border-[#E0EDE0] rounded-xl px-4 py-3" data-testid={`glossary-${g.term}`}>
            <h4 className="text-sm font-bold text-[#1F4E2E] mb-1">{g.term}</h4>
            <p className="text-[13px] text-[#555] leading-relaxed">{g.definition}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


function WriteMyResults() {
  const [form, setForm] = useState({ analysis_type: '', crop: '', trait: '', key_finding: '', stat_values: '', significance: '', practical_implication: '' });
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult('');
    try {
      const res = await fetch(`${API}/api/write-results`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      setResult(data.paragraph || data.error || 'Failed to generate.');
    } catch { setResult('Network error.'); }
    setLoading(false);
  };

  const copyText = () => { navigator.clipboard.writeText(result); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const fields = [
    { key: 'analysis_type', label: 'Analysis Type', placeholder: 'e.g., Diallel Analysis (Griffing Method II)' },
    { key: 'crop', label: 'Crop', placeholder: 'e.g., Bread wheat (Triticum aestivum L.)' },
    { key: 'trait', label: 'Trait', placeholder: 'e.g., Grain yield (q/ha)' },
    { key: 'key_finding', label: 'Key Finding', placeholder: 'e.g., Parent P3 had the highest positive GCA effect' },
    { key: 'stat_values', label: 'Statistical Values', placeholder: 'e.g., GCA=+4.32, SCA for P2×P3=+1.60, h²=23.56%' },
    { key: 'significance', label: 'Significance Level', placeholder: 'e.g., Significant at p<0.01 (**)' },
    { key: 'practical_implication', label: 'Practical Implication', placeholder: 'e.g., P2×P3 is doubly recommended for hybrid breeding' },
  ];

  return (
    <div className="max-w-2xl mx-auto py-6" data-testid="write-results">
      <p className="text-xs text-[#888] mb-4">Fill in the fields below. ONE short AI call generates a publication-ready paragraph.</p>
      <form onSubmit={handleSubmit} className="space-y-3">
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-[12px] font-semibold text-[#1F4E2E] mb-1 block">{f.label}</label>
            <input type="text" required value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder}
              className="w-full px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57] bg-white" data-testid={`wr-${f.key}`} />
          </div>
        ))}
        <button type="submit" disabled={loading} className="w-full py-2.5 bg-[#2D8B57] text-white rounded-lg text-sm font-medium hover:bg-[#246E47] disabled:opacity-50 flex items-center justify-center gap-2 transition" data-testid="wr-submit">
          {loading ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Send size={14} />}
          {loading ? 'Generating...' : 'Generate Paragraph'}
        </button>
      </form>
      {result && (
        <div className="mt-5 bg-[#F4FAF5] border border-[#D0E8D0] rounded-xl p-4 relative" data-testid="wr-output">
          <button onClick={copyText} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[#D0E8D0] transition" title="Copy">
            {copied ? <CheckCheck size={14} className="text-[#2D8B57]" /> : <Copy size={14} className="text-[#888]" />}
          </button>
          <p className="text-sm text-[#333] leading-relaxed pr-8">{result}</p>
        </div>
      )}
    </div>
  );
}


function ResearchInspiration() {
  const [selectedTopic, setSelectedTopic] = useState(0);
  const [hypoForm, setHypoForm] = useState({ crop: '', field: '', current_gap: '' });
  const [hypotheses, setHypotheses] = useState('');
  const [loading, setLoading] = useState(false);

  const generateHypotheses = async (e) => {
    e.preventDefault();
    setLoading(true); setHypotheses('');
    try {
      const res = await fetch(`${API}/api/generate-hypothesis`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(hypoForm),
      });
      const data = await res.json();
      setHypotheses(data.hypotheses || data.error || 'Failed.');
    } catch { setHypotheses('Network error.'); }
    setLoading(false);
  };

  return (
    <div className="py-4 space-y-6" data-testid="research-inspiration">
      {/* Weekly Topics */}
      <div>
        <h3 className="text-base font-bold text-[#1F4E2E] mb-3 flex items-center gap-2"><Lightbulb size={16} className="text-[#E67E22]" /> Weekly Research Ideas</h3>
        <div className="flex gap-2 flex-wrap mb-4">
          {WEEKLY_TOPICS.map((t, i) => (
            <button key={i} onClick={() => setSelectedTopic(i)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${selectedTopic === i ? 'bg-[#2D8B57] text-white' : 'bg-white border border-[#E0EDE0] text-[#555] hover:border-[#2D8B57]'}`}>{t.week}</button>
          ))}
        </div>
        <div className="bg-white border border-[#E0EDE0] rounded-xl p-4">
          <h4 className="text-sm font-bold text-[#1F4E2E] mb-3">{WEEKLY_TOPICS[selectedTopic].week}</h4>
          <ul className="space-y-2">
            {WEEKLY_TOPICS[selectedTopic].ideas.map((idea, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#333]">
                <span className="w-5 h-5 rounded-full bg-[#2D8B57]/10 text-[#2D8B57] flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                {idea}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Hypothesis Generator (1 AI call) */}
      <div className="bg-white border border-[#E0EDE0] rounded-xl p-4">
        <h3 className="text-sm font-bold text-[#1F4E2E] mb-3 flex items-center gap-2"><Sparkles size={14} className="text-[#C0392B]" /> AI Hypothesis Generator</h3>
        <p className="text-[11px] text-[#888] mb-3">One AI call generates 3 testable hypotheses for your PhD research.</p>
        <form onSubmit={generateHypotheses} className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <input type="text" required value={hypoForm.crop} onChange={e => setHypoForm({ ...hypoForm, crop: e.target.value })} placeholder="Crop (e.g., Wheat)" className="px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="hypo-crop" />
          <input type="text" required value={hypoForm.field} onChange={e => setHypoForm({ ...hypoForm, field: e.target.value })} placeholder="Field (e.g., Combining ability)" className="px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="hypo-field" />
          <input type="text" required value={hypoForm.current_gap} onChange={e => setHypoForm({ ...hypoForm, current_gap: e.target.value })} placeholder="Research gap" className="px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="hypo-gap" />
        </form>
        <button onClick={generateHypotheses} disabled={loading || !hypoForm.crop || !hypoForm.field || !hypoForm.current_gap} className="px-5 py-2 bg-[#C0392B] text-white rounded-lg text-sm font-medium hover:bg-[#A93226] disabled:opacity-50 flex items-center gap-2 transition" data-testid="hypo-submit">
          {loading ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Sparkles size={14} />}
          {loading ? 'Generating...' : 'Generate Hypotheses'}
        </button>
        {hypotheses && (
          <div className="mt-3 bg-[#FFF5F5] border border-[#F5C6C6] rounded-lg p-3" data-testid="hypo-output">
            <p className="text-sm text-[#333] whitespace-pre-line leading-relaxed">{hypotheses}</p>
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════
   WRITE MY PAPER — 5-Step Research Paper Generator
   ═══════════════════════════════════════════════════ */
const CROPS = ['Wheat','Rice','Maize','Mustard','Cotton','Chickpea','Soybean','Groundnut','Sorghum','Pearl Millet','Other'];
const JOURNALS = ['Nature Plants','Crop Science','Field Crops Research','Indian Journal of Genetics and Plant Breeding','Euphytica','SABRAO Journal','Plant Breeding','Theoretical and Applied Genetics','PLOS ONE','Other'];
const CITE_STYLES = ['APA','MLA','Vancouver','CSE'];
const PAPER_SECTIONS = [
  { key: 'title', label: 'Title Suggestions', order: 1 },
  { key: 'abstract', label: 'Abstract', order: 2 },
  { key: 'keywords', label: 'Keywords', order: 3 },
  { key: 'introduction', label: 'Introduction', order: 4 },
  { key: 'methods', label: 'Materials and Methods', order: 5 },
  { key: 'results', label: 'Results', order: 6 },
  { key: 'discussion', label: 'Discussion', order: 7 },
  { key: 'conclusion', label: 'Conclusion', order: 8 },
  { key: 'references', label: 'References', order: 9 },
];

function WriteMyPaper() {
  const [step, setStep] = useState(1);
  const [setup, setSetup] = useState({ crop: '', topic: '', journal: '', degree: 'None', university: '' });
  const [uploadedTables, setUploadedTables] = useState([]); // [{id, label, type, headers, rows, file}]
  const [suppFiles, setSuppFiles] = useState([]); // [{id, label, type, file, size}]
  const [findingsText, setFindingsText] = useState('');
  const [figures, setFigures] = useState([]);
  const [tablePreview, setTablePreview] = useState(null); // id of table being previewed
  const [citations, setCitations] = useState([]);
  const [citLoading, setCitLoading] = useState(false);
  const [extraDoi, setExtraDoi] = useState('');
  const [citeStyle, setCiteStyle] = useState('APA');
  const [sections, setSections] = useState({});
  const [genLoading, setGenLoading] = useState({});
  const [openPanel, setOpenPanel] = useState(null);
  const [copyState, setCopyState] = useState('');
  const [exportLoading, setExportLoading] = useState('');
  // New state for 5 additions
  const [plagiarism, setPlagiarism] = useState(null);
  const [plagLoading, setPlagLoading] = useState(false);
  const [journalFit, setJournalFit] = useState(null);
  const [jfLoading, setJfLoading] = useState(false);
  const [reviewerSim, setReviewerSim] = useState(null);
  const [revLoading, setRevLoading] = useState(false);
  const [hindiAbstract, setHindiAbstract] = useState('');
  const [hindiLoading, setHindiLoading] = useState(false);

  // ── Table/file upload handlers ──
  const parseCSVText = (text) => {
    const sep = text.includes('\t') ? '\t' : ',';
    const lines = text.trim().split('\n').map(l => l.split(sep).map(c => c.trim().replace(/^"|"$/g, '')));
    if (lines.length < 2) return null;
    return { headers: lines[0], rows: lines.slice(1) };
  };

  const handleTableFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseCSVText(text);
        if (parsed) {
          setUploadedTables(prev => [...prev, {
            id: Date.now() + Math.random(), label: `Table ${prev.length + 1} — ${file.name.replace(/\.\w+$/, '')}`,
            type: 'Custom Table', headers: parsed.headers, rows: parsed.rows, file,
          }]);
        }
      } catch {}
    };
    if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
      reader.readAsText(file);
    } else {
      // For xlsx, store with placeholder — real parsing would need xlsx library
      setUploadedTables(prev => [...prev, {
        id: Date.now() + Math.random(), label: `Table ${prev.length + 1} — ${file.name.replace(/\.\w+$/, '')}`,
        type: 'Custom Table', headers: ['(Excel file — preview after export)'], rows: [], file,
      }]);
    }
  };

  const handlePastedTable = (text) => {
    const parsed = parseCSVText(text);
    if (parsed) {
      setUploadedTables(prev => [...prev, {
        id: Date.now() + Math.random(), label: `Table ${prev.length + 1} — Pasted data`,
        type: 'Custom Table', headers: parsed.headers, rows: parsed.rows, file: null,
      }]);
    }
  };

  const handleSuppFile = (file) => {
    setSuppFiles(prev => [...prev, {
      id: Date.now() + Math.random(), label: file.name.replace(/\.\w+$/, ''),
      type: 'Raw Data', file, size: file.size,
    }]);
  };

  // ── Feature 1: Auto-detect significance from ANOVA ──
  const autoDetectSignificance = (anovaIdx) => {
    const anova = uploadedTables[anovaIdx];
    if (!anova || !anova.headers || !anova.rows) return;
    const hLower = anova.headers.map(h => h.toLowerCase().trim());
    const pIdx = hLower.findIndex(h => h.includes('p-val') || h.includes('p val') || h.includes('prob') || h === 'p' || h === 'sig');
    const fIdx = hLower.findIndex(h => h.includes('f val') || h.includes('f-val') || h === 'f');
    if (pIdx < 0 && fIdx < 0) return;
    const sigMap = {};
    for (const row of anova.rows) {
      const source = (row[0] || '').trim();
      let pVal = pIdx >= 0 ? parseFloat(row[pIdx]) : NaN;
      if (isNaN(pVal) && fIdx >= 0) { pVal = parseFloat(row[fIdx]) > 5 ? 0.001 : 0.1; }
      if (!isNaN(pVal)) {
        sigMap[source] = pVal < 0.01 ? '**' : pVal < 0.05 ? '*' : 'NS';
      }
    }
    // Apply stars to all means tables
    const updated = uploadedTables.map((t, i) => {
      if (i === anovaIdx || t.type !== 'Means Comparison') return t;
      const newHeaders = [...t.headers];
      if (!newHeaders.includes('Sig')) newHeaders.push('Sig');
      const sigColIdx = newHeaders.indexOf('Sig');
      const newRows = t.rows.map(r => {
        const row = [...r];
        while (row.length < newHeaders.length) row.push('');
        const bestSig = Object.values(sigMap).find(s => s === '**') || Object.values(sigMap).find(s => s === '*') || 'NS';
        row[sigColIdx] = bestSig;
        return row;
      });
      return { ...t, headers: newHeaders, rows: newRows, sigApplied: true };
    });
    setUploadedTables(updated);
  };

  // ── Feature 2: Merge two tables ──
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState([]);
  const mergeTables = () => {
    if (mergeSelection.length !== 2) return;
    const [a, b] = mergeSelection.map(idx => uploadedTables[idx]);
    if (!a || !b) return;
    const commonCols = a.headers.filter(h => b.headers.includes(h));
    const keyCol = commonCols[0] || a.headers[0];
    const allHeaders = [...a.headers, ...b.headers.filter(h => !a.headers.includes(h))];
    const aKeyIdx = a.headers.indexOf(keyCol);
    const bKeyIdx = b.headers.indexOf(keyCol);
    const bMap = {};
    for (const row of b.rows) { bMap[row[bKeyIdx]] = row; }
    const mergedRows = a.rows.map(aRow => {
      const key = aRow[aKeyIdx];
      const bRow = bMap[key] || [];
      const merged = [...aRow];
      for (const h of b.headers) {
        if (!a.headers.includes(h)) {
          const bi = b.headers.indexOf(h);
          merged.push(bRow[bi] || '');
        }
      }
      return merged;
    });
    // Add b rows not in a
    for (const bRow of b.rows) {
      if (!a.rows.some(ar => ar[aKeyIdx] === bRow[bKeyIdx])) {
        const merged = allHeaders.map(h => {
          const ai = a.headers.indexOf(h);
          const bi = b.headers.indexOf(h);
          return bi >= 0 ? (bRow[bi] || '') : '';
        });
        mergedRows.push(merged);
      }
    }
    const merged = {
      id: Date.now() + Math.random(),
      label: `Table ${uploadedTables.length + 1} — Merged (${a.label.split('—')[1]?.trim() || 'A'} + ${b.label.split('—')[1]?.trim() || 'B'})`,
      type: 'Custom Table', headers: allHeaders, rows: mergedRows, file: null,
    };
    setUploadedTables(prev => [...prev, merged]);
    setMergeMode(false); setMergeSelection([]);
  };

  // ── Feature 3: CD/SE auto-calculator ──
  const calculateCD = (anovaIdx, meansIdx) => {
    const anova = uploadedTables[anovaIdx];
    const means = uploadedTables[meansIdx];
    if (!anova || !means) return;
    const hLower = anova.headers.map(h => h.toLowerCase().trim());
    const msIdx = hLower.findIndex(h => h.includes('ms') || h.includes('mean sq'));
    const dfIdx = hLower.findIndex(h => h.includes('df') || h === 'df');
    const srcIdx = 0;
    let mse = NaN, dfe = NaN;
    for (const row of anova.rows) {
      const src = (row[srcIdx] || '').toLowerCase();
      if (src.includes('error') || src.includes('residual') || src.includes('within')) {
        if (msIdx >= 0) mse = parseFloat(row[msIdx]);
        if (dfIdx >= 0) dfe = parseFloat(row[dfIdx]);
      }
    }
    if (isNaN(mse)) return;
    const r = 3; // default reps assumption
    const se = Math.sqrt(mse / r);
    const seDiff = Math.sqrt(2 * mse / r);
    const tVal5 = dfe > 0 ? 2.0 : 1.96; // approximate
    const tVal1 = dfe > 0 ? 2.7 : 2.576;
    const cd5 = (seDiff * tVal5).toFixed(3);
    const cd1 = (seDiff * tVal1).toFixed(3);
    // Append CD/SE row to means table
    const updated = [...uploadedTables];
    const mt = { ...means };
    mt.rows = [...mt.rows, ['SE(m)', se.toFixed(3), '', '', ''], ['SE(d)', seDiff.toFixed(3), '', '', ''], ['CD (5%)', cd5, '', '', ''], ['CD (1%)', cd1, '', '', '']];
    mt.cdCalculated = true;
    updated[meansIdx] = mt;
    setUploadedTables(updated);
  };

  const stepLabels = ['Paper Setup','Upload Results','Citations','Generate Paper','Export'];
  const completion = (() => {
    let done = 0;
    if (setup.crop && setup.topic && setup.journal) done += 20;
    if (uploadedTables.length > 0 || findingsText) done += 20;
    if (citations.some(c => c.included)) done += 15;
    const genCount = Object.keys(sections).length;
    done += Math.min(genCount * 5, 45);
    return Math.min(done, 100);
  })();

  // Quality score
  const qualityScore = (() => {
    let score = 0;
    const abs = sections.abstract || '';
    const absWords = abs.split(/\s+/).filter(Boolean).length;
    if (absWords >= 200 && absWords <= 300) score += 15; else if (absWords > 100) score += 8;
    const incCitations = citations.filter(c => c.included).length;
    if (incCitations >= 10) score += 15; else if (incCitations >= 5) score += 8;
    if (figures.length > 0) score += 10;
    if (sections.results && figures.length > 0 && sections.results.includes('Figure')) score += 10;
    if (sections.results && /p\s*[<>]/i.test(sections.results)) score += 10;
    if (sections.methods) score += 10;
    if (sections.introduction) score += 10;
    if (sections.discussion) score += 10;
    if (sections.conclusion) score += 5;
    if (sections.references) score += 5;
    return Math.min(score, 100);
  })();

  const qualityFlags = [];
  const abs = sections.abstract || '';
  const absWords = abs.split(/\s+/).filter(Boolean).length;
  if (sections.abstract && (absWords < 200 || absWords > 300)) qualityFlags.push(`Abstract: ${absWords} words (target 230-250)`);
  if (citations.filter(c => c.included).length < 10) qualityFlags.push('Citations: fewer than 10 included (minimum 25 recommended)');
  if (figures.length === 0) qualityFlags.push('No figures uploaded');
  if (sections.results && figures.length > 0 && !sections.results.includes('Figure')) qualityFlags.push('Figures not referenced in Results text');
  if (!sections.methods) qualityFlags.push('Materials and Methods not generated');

  // Build a text summary from uploaded tables for AI consumption
  const buildResultsText = () => {
    let txt = '';
    if (uploadedTables.length > 0) {
      txt += uploadedTables.map((t, i) => {
        const hdr = (t.headers || []).join(' | ');
        const bodyRows = (t.rows || []).slice(0, 5).map(r => r.join(' | ')).join('\n');
        return `Table ${i + 1} (${t.type || 'Data'}): ${t.label}\nColumns: ${hdr}\n${bodyRows}${(t.rows || []).length > 5 ? `\n... (${t.rows.length} rows total)` : ''}`;
      }).join('\n\n');
    }
    return txt;
  };

  const buildSuppText = () => {
    if (suppFiles.length === 0) return '';
    return '\n\nSupplementary tables available: ' + suppFiles.map((f, i) => `Table S${i + 1}: ${f.label} (${f.type})`).join('; ') + '. Cite them as (Table S1), (Table S2) etc. in the text where relevant.';
  };

  const suggestCitations = async () => {
    setCitLoading(true);
    try {
      const res = await fetch(`${API}/api/paper/suggest-citations`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ crop: setup.crop, topic: setup.topic }),
      });
      const data = await res.json();
      const cits = (data.citations || []).map((c, i) => ({ ...c, id: i + 1, included: true }));
      setCitations(cits);
    } catch { setCitations([]); }
    setCitLoading(false);
  };

  const generateSection = async (sectionKey) => {
    setGenLoading(p => ({ ...p, [sectionKey]: true }));
    const citText = citations.filter(c => c.included).map(c => `${c.authors} (${c.year}). ${c.title}. ${c.journal}.`).join('\n');
    try {
      const res = await fetch(`${API}/api/paper/generate-section`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          section: sectionKey, crop: setup.crop, topic: setup.topic, journal: setup.journal,
          degree: setup.degree, university: setup.university,
          results_text: buildResultsText() + '\n' + buildSuppText(),
          findings_text: findingsText, citations_text: citText, citation_style: citeStyle,
          figure_count: figures.length,
          table_count: uploadedTables.length,
          supp_info: suppFiles.length > 0 ? `Also cite supplementary tables as (Table S1), (Table S2) etc: ${suppFiles.map((f, i) => `Table S${i+1}: ${f.label}`).join('; ')}` : '',
        }),
      });
      const data = await res.json();
      setSections(p => ({ ...p, [sectionKey]: data.content || data.error || '' }));
      setOpenPanel(sectionKey);
    } catch (e) { setSections(p => ({ ...p, [sectionKey]: 'Network error.' })); }
    setGenLoading(p => ({ ...p, [sectionKey]: false }));
  };

  const generateAll = async () => {
    for (const s of PAPER_SECTIONS) {
      if (!sections[s.key]) await generateSection(s.key);
    }
  };

  const runPlagiarismCheck = async () => {
    setPlagLoading(true); setPlagiarism(null);
    const allText = PAPER_SECTIONS.map(s => sections[s.key] || '').join('\n\n');
    try {
      const res = await fetch(`${API}/api/paper/plagiarism-check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ text: allText }),
      });
      setPlagiarism(await res.json());
    } catch { setPlagiarism({ score: 0, flags: ['Network error'], summary: 'Check failed' }); }
    setPlagLoading(false);
  };

  const runJournalFit = async () => {
    setJfLoading(true); setJournalFit(null);
    const allText = PAPER_SECTIONS.map(s => sections[s.key] || '').join('\n\n');
    try {
      const res = await fetch(`${API}/api/paper/journal-fit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ text: allText, journal: setup.journal, crop: setup.crop, topic: setup.topic }),
      });
      setJournalFit(await res.json());
    } catch { setJournalFit({ score: 0, strengths: [], weaknesses: ['Network error'], recommendation: 'Check failed' }); }
    setJfLoading(false);
  };

  const runReviewerSim = async () => {
    setRevLoading(true); setReviewerSim(null);
    try {
      const res = await fetch(`${API}/api/paper/reviewer-simulation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ crop: setup.crop, topic: setup.topic, journal: setup.journal, abstract: sections.abstract || '', results_summary: sections.results || '', methods_summary: sections.methods || '' }),
      });
      setReviewerSim(await res.json());
    } catch { setReviewerSim({ reviews: [{ reviewer: 1, comment: 'Network error', severity: 'Critical', fix: 'Check connection' }] }); }
    setRevLoading(false);
  };

  const generateHindiAbstract = async () => {
    setHindiLoading(true); setHindiAbstract('');
    const citText = citations.filter(c => c.included).map(c => `${c.authors} (${c.year})`).join(', ');
    try {
      const res = await fetch(`${API}/api/paper/generate-section`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ section: 'abstract_hindi', crop: setup.crop, topic: setup.topic, journal: setup.journal, results_text: buildResultsText(), findings_text: findingsText, citations_text: citText, citation_style: citeStyle, figure_count: figures.length }),
      });
      const data = await res.json();
      const content = data.content || '';
      setHindiAbstract(content);
      setSections(p => ({ ...p, abstract_hindi: content }));
    } catch { setHindiAbstract('Network error.'); }
    setHindiLoading(false);
  };

  const exportDocx = async () => {
    setExportLoading('docx');
    try {
      const formData = new FormData();
      const secsCopy = { ...sections };
      if (hindiAbstract) secsCopy.abstract_hindi = hindiAbstract;
      formData.append('sections_json', JSON.stringify(secsCopy));
      formData.append('tables_json', JSON.stringify(uploadedTables.map((t, i) => ({ label: t.label, type: t.type, headers: t.headers, rows: t.rows }))));
      formData.append('journal', setup.journal);
      formData.append('crop', setup.crop);
      formData.append('topic', setup.topic);
      formData.append('figure_names', figures.map(f => f.name).join(','));
      for (const fig of figures) {
        formData.append('files', fig);
      }
      const res = await fetch(`${API}/api/paper/export-docx`, { method: 'POST', credentials: 'include', body: formData });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `BKAgriD_Paper_${setup.crop}_${setup.topic.slice(0, 20).replace(/ /g, '_')}.docx`;
        a.click(); URL.revokeObjectURL(url);
      }
    } catch (e) { console.error(e); }
    setExportLoading('');
  };

  const exportPdf = () => { exportDocx(); };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopyState(key);
    setTimeout(() => setCopyState(''), 2000);
  };

  const thCls = "text-[13px] font-bold text-[#1F4E2E]";
  const cardCls = "bg-white border border-[#E0EDE0] rounded-xl p-4";
  const btnPrimary = "px-5 py-2.5 bg-[#2D8B57] text-white rounded-lg text-sm font-medium hover:bg-[#246E47] disabled:opacity-50 transition flex items-center gap-2";
  const btnSecondary = "px-4 py-2 bg-white border border-[#E0EDE0] text-[#333] rounded-lg text-sm font-medium hover:border-[#2D8B57] transition flex items-center gap-2";

  return (
    <div className="py-4 max-w-[1100px] mx-auto" data-testid="write-my-paper">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {stepLabels.map((l, i) => (
            <button key={i} onClick={() => setStep(i + 1)} className={`text-[11px] font-medium transition ${step === i + 1 ? 'text-[#2D8B57] font-bold' : i + 1 < step ? 'text-[#2D8B57]/60' : 'text-[#aaa]'}`} data-testid={`paper-step-${i+1}`}>
              <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-[10px] mr-1 ${step === i + 1 ? 'bg-[#2D8B57] text-white' : i + 1 < step ? 'bg-[#2D8B57]/20 text-[#2D8B57]' : 'bg-[#eee] text-[#999]'}`}>{i + 1}</span>
              <span className="hidden sm:inline">{l}</span>
            </button>
          ))}
          <span className="text-[11px] font-bold text-[#2D8B57]">{completion}%</span>
        </div>
        <div className="w-full h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#2D8B57] to-[#16A085] rounded-full transition-all duration-500" style={{ width: `${completion}%` }} />
        </div>
      </div>

      {/* ── STEP 1: Paper Setup ── */}
      {step === 1 && (
        <div className={cardCls} data-testid="paper-step1">
          <h3 className={`${thCls} text-base mb-4`}>Step 1 — Paper Setup</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-semibold text-[#1F4E2E] mb-1 block">Crop Name *</label>
              <select value={setup.crop} onChange={e => setSetup({ ...setup, crop: e.target.value })} className="w-full px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg bg-white focus:outline-none focus:border-[#2D8B57]" data-testid="paper-crop">
                <option value="">Select crop...</option>
                {CROPS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#1F4E2E] mb-1 block">Target Journal *</label>
              <select value={setup.journal} onChange={e => setSetup({ ...setup, journal: e.target.value })} className="w-full px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg bg-white focus:outline-none focus:border-[#2D8B57]" data-testid="paper-journal">
                <option value="">Select journal...</option>
                {JOURNALS.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[12px] font-semibold text-[#1F4E2E] mb-1 block">Research Topic *</label>
              <input type="text" value={setup.topic} onChange={e => setSetup({ ...setup, topic: e.target.value })} placeholder="e.g., Combining ability analysis for grain yield in bread wheat" className="w-full px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="paper-topic" />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#1F4E2E] mb-1 block">Degree Type</label>
              <select value={setup.degree} onChange={e => setSetup({ ...setup, degree: e.target.value })} className="w-full px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg bg-white focus:outline-none focus:border-[#2D8B57]" data-testid="paper-degree">
                {['None','MSc','PhD'].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#1F4E2E] mb-1 block">University</label>
              <input type="text" value={setup.university} onChange={e => setSetup({ ...setup, university: e.target.value })} placeholder="Optional" className="w-full px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="paper-uni" />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button disabled={!setup.crop || !setup.topic || !setup.journal} onClick={() => setStep(2)} className={btnPrimary} data-testid="paper-next1">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Upload Results ── */}
      {step === 2 && (
        <div data-testid="paper-step2">
          <h3 className={`${thCls} text-base mb-4`}>Step 2 — Upload Your Results</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* ─── Upload Tables ─── */}
            <div className={`${cardCls} lg:col-span-2`} data-testid="paper-tables-section">
              <h4 className="text-[13px] font-bold text-[#1F4E2E] mb-3 flex items-center gap-1.5"><Table2 size={14} /> Upload Tables <span className="text-[10px] font-normal text-[#888] ml-1">(up to 10)</span></h4>
              {uploadedTables.length < 10 && (
                <div
                  className="border-2 border-dashed border-[#D0E8D0] rounded-xl p-6 text-center hover:border-[#2D8B57] hover:bg-[#F4FAF5] transition cursor-pointer mb-3"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-[#2D8B57]','bg-[#F4FAF5]'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('border-[#2D8B57]','bg-[#F4FAF5]'); }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-[#2D8B57]','bg-[#F4FAF5]'); const f = e.dataTransfer.files[0]; if(f) handleTableFile(f); }}
                  onClick={() => document.getElementById('table-file-input')?.click()}
                  data-testid="table-dropzone"
                >
                  <Upload size={20} className="text-[#2D8B57] mx-auto mb-1" />
                  <p className="text-[12px] text-[#555]">Drag & drop CSV/Excel file or <span className="text-[#2D8B57] font-medium">click to browse</span></p>
                  <p className="text-[10px] text-[#aaa] mt-1">Accepts .csv, .xlsx, .xls — or paste tab-separated table below</p>
                  <input id="table-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => { if(e.target.files[0]) handleTableFile(e.target.files[0]); e.target.value=''; }} />
                </div>
              )}
              {/* Paste zone */}
              <textarea placeholder="Or paste table data here (tab-separated or comma-separated)..." rows={2}
                className="w-full px-3 py-2 text-[11px] border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57] font-mono resize-none mb-3"
                onPaste={e => { const text = e.clipboardData.getData('text'); if(text && text.includes('\t') || text.includes(',')) { e.preventDefault(); handlePastedTable(text); } }}
                data-testid="table-paste" />
              {/* Table list */}
              {uploadedTables.length > 0 && (
                <div className="space-y-2">
                  {uploadedTables.map((t, idx) => (
                    <div key={t.id} className="border border-[#E0EDE0] rounded-xl p-3 bg-[#FAFFFE]" data-testid={`table-item-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <GripVertical size={14} className="text-[#ccc] cursor-grab shrink-0" />
                        <Check size={14} className="text-[#2D8B57] shrink-0" />
                        <span className="text-[11px] font-bold text-[#2D8B57] bg-[#E8F5E9] px-2 py-0.5 rounded shrink-0">Table {idx + 1}</span>
                        <input type="text" value={t.label} onChange={e => { const up = [...uploadedTables]; up[idx] = {...t, label: e.target.value}; setUploadedTables(up); }}
                          placeholder={`Table ${idx + 1} — Description`}
                          className="flex-1 px-2 py-1 text-[12px] border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" />
                        <select value={t.type} onChange={e => { const up = [...uploadedTables]; up[idx] = {...t, type: e.target.value}; setUploadedTables(up); }}
                          className="px-2 py-1 text-[11px] border border-[#E0EDE0] rounded-lg bg-white focus:outline-none focus:border-[#2D8B57] shrink-0">
                          {['ANOVA Table','Means Comparison','Correlation Matrix','Stability Parameters','Genetic Parameters','Combining Ability','Frequency Distribution','Descriptive Statistics','Custom Table'].map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <button onClick={() => setTablePreview(tablePreview === t.id ? null : t.id)} className="p-1 rounded hover:bg-[#E8F5E9] transition" title="Preview">
                          {tablePreview === t.id ? <EyeOff size={13} className="text-[#888]" /> : <Eye size={13} className="text-[#2D8B57]" />}
                        </button>
                        <button onClick={() => { if(idx > 0){ const up=[...uploadedTables]; [up[idx-1],up[idx]]=[up[idx],up[idx-1]]; setUploadedTables(up); }}} className="p-1 rounded hover:bg-[#E8F5E9] transition" title="Move up">
                          <ChevronDown size={13} className="text-[#888] rotate-180" />
                        </button>
                        <button onClick={() => setUploadedTables(uploadedTables.filter(x => x.id !== t.id))} className="p-1 rounded hover:bg-red-50 transition" title="Remove">
                          <Trash2 size={13} className="text-[#C0392B]" />
                        </button>
                      </div>
                      {/* Smart action bar */}
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        {t.type === 'ANOVA Table' && uploadedTables.some(x => x.type === 'Means Comparison') && (
                          <button onClick={() => autoDetectSignificance(idx)} className="text-[10px] px-2 py-0.5 bg-[#E8F5E9] text-[#2D8B57] rounded border border-[#D0E8D0] hover:bg-[#D0E8D0] transition flex items-center gap-1" data-testid={`auto-sig-${idx}`}>
                            <Sparkles size={9} /> Auto-add significance stars
                          </button>
                        )}
                        {t.type === 'ANOVA Table' && uploadedTables.some(x => x.type === 'Means Comparison' && !x.cdCalculated) && (
                          <button onClick={() => { const mi = uploadedTables.findIndex(x => x.type === 'Means Comparison' && !x.cdCalculated); if(mi>=0) calculateCD(idx, mi); }}
                            className="text-[10px] px-2 py-0.5 bg-[#FFF8E1] text-[#E67E22] rounded border border-[#FFE082] hover:bg-[#FFE082] transition flex items-center gap-1" data-testid={`calc-cd-${idx}`}>
                            <Sparkles size={9} /> Calculate CD/SE for Means table
                          </button>
                        )}
                        {t.cdCalculated && <span className="text-[10px] text-[#2D8B57] flex items-center gap-1"><Check size={9} /> CD/SE added</span>}
                        {t.sigApplied && <span className="text-[10px] text-[#2D8B57] flex items-center gap-1"><Check size={9} /> Significance applied</span>}
                      </div>
                      {/* Formatted preview */}
                      {tablePreview === t.id && t.headers && (
                        <div className="overflow-x-auto mt-2 border-t border-b border-[#333] py-1">
                          <p className="text-[11px] italic text-[#555] mb-1">Table {idx + 1}. {t.label || 'Untitled'}</p>
                          <table className="w-full text-[11px] font-mono">
                            <thead>
                              <tr className="border-b border-[#333]">
                                {t.headers.map((h, hi) => <th key={hi} className="px-2 py-1 text-left font-bold">{h}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {(t.rows || []).slice(0, 8).map((row, ri) => (
                                <tr key={ri} className={ri === (t.rows||[]).slice(0,8).length - 1 ? 'border-b border-[#333]' : ''}>
                                  {row.map((cell, ci) => <td key={ci} className="px-2 py-0.5">{cell}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {(t.rows||[]).length > 8 && <p className="text-[10px] text-[#888] mt-1">Showing 8 of {t.rows.length} rows</p>}
                          <p className="text-[10px] text-[#888] mt-1 italic">* P{'<'}0.05, ** P{'<'}0.01</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Merge Tables Tool ─── */}
            {uploadedTables.length >= 2 && (
              <div className={`${cardCls} lg:col-span-2`} data-testid="merge-tables">
                <h4 className="text-[12px] font-bold text-[#1F4E2E] mb-2 flex items-center gap-1.5"><Table2 size={13} /> Merge Two Tables</h4>
                {!mergeMode ? (
                  <button onClick={() => setMergeMode(true)} className="text-[11px] px-3 py-1.5 bg-[#F4FAF5] text-[#2D8B57] rounded-lg border border-[#D0E8D0] hover:bg-[#E8F5E9] transition">Select 2 tables to merge into one publication-ready table</button>
                ) : (
                  <div>
                    <p className="text-[11px] text-[#555] mb-2">Select exactly 2 tables to merge (joined on first common column):</p>
                    <div className="flex gap-2 flex-wrap mb-2">
                      {uploadedTables.map((t, idx) => (
                        <button key={t.id} onClick={() => setMergeSelection(prev => prev.includes(idx) ? prev.filter(x => x !== idx) : prev.length < 2 ? [...prev, idx] : prev)}
                          className={`text-[11px] px-3 py-1 rounded-lg border transition ${mergeSelection.includes(idx) ? 'bg-[#2D8B57] text-white border-[#2D8B57]' : 'bg-white border-[#E0EDE0] text-[#555] hover:border-[#2D8B57]'}`}>
                          Table {idx + 1}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={mergeTables} disabled={mergeSelection.length !== 2} className="text-[11px] px-3 py-1.5 bg-[#2D8B57] text-white rounded-lg disabled:opacity-40 transition">Merge Selected</button>
                      <button onClick={() => { setMergeMode(false); setMergeSelection([]); }} className="text-[11px] px-3 py-1.5 bg-white border border-[#E0EDE0] text-[#555] rounded-lg hover:border-[#C0392B] transition">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ─── Upload Figures (preserved) ─── */}
            <div className={cardCls}>
              <h4 className="text-[12px] font-bold text-[#1F4E2E] mb-2 flex items-center gap-1.5"><Upload size={13} /> Upload Figures</h4>
              <input type="file" multiple accept=".jpg,.jpeg,.png" onChange={e => setFigures([...figures, ...Array.from(e.target.files)])} className="w-full text-[12px] mb-2" data-testid="paper-figures" />
              {figures.length > 0 && <p className="text-[11px] text-[#2D8B57]">{figures.length} figure(s) uploaded</p>}
              <div className="flex flex-wrap gap-1 mt-1">
                {figures.map((f, i) => (
                  <span key={i} className="text-[10px] bg-[#F4FAF5] text-[#2D8B57] px-2 py-0.5 rounded border border-[#D0E8D0] flex items-center gap-1">
                    Fig {i + 1}: {f.name.slice(0, 15)}
                    <button onClick={() => setFigures(figures.filter((_, fi) => fi !== i))} className="hover:text-[#C0392B]"><Trash2 size={9} /></button>
                  </span>
                ))}
              </div>
            </div>

            {/* ─── Supplementary Data ─── */}
            <div className={cardCls} data-testid="paper-supp-section">
              <h4 className="text-[12px] font-bold text-[#1F4E2E] mb-2 flex items-center gap-1.5"><FileText size={13} /> Supplementary Data <span className="text-[10px] font-normal text-[#888] ml-1">(up to 5)</span></h4>
              {suppFiles.length < 5 && (
                <div className="border-2 border-dashed border-[#E0EDE0] rounded-lg p-4 text-center hover:border-[#2D8B57] hover:bg-[#F4FAF5] transition cursor-pointer mb-2"
                  onClick={() => document.getElementById('supp-file-input')?.click()}>
                  <Upload size={16} className="text-[#888] mx-auto mb-1" />
                  <p className="text-[11px] text-[#555]">Upload CSV, Excel, or PDF</p>
                  <input id="supp-file-input" type="file" accept=".csv,.xlsx,.xls,.pdf" className="hidden" onChange={e => { if(e.target.files[0]) handleSuppFile(e.target.files[0]); e.target.value=''; }} />
                </div>
              )}
              {suppFiles.length > 0 && (
                <div className="space-y-1.5">
                  {suppFiles.map((sf, i) => (
                    <div key={sf.id} className="flex items-center gap-2 bg-[#FAFFFE] border border-[#E0EDE0] rounded-lg px-3 py-2">
                      <Check size={12} className="text-[#2D8B57] shrink-0" />
                      <input type="text" value={sf.label} onChange={e => { const up=[...suppFiles]; up[i]={...sf,label:e.target.value}; setSuppFiles(up); }}
                        placeholder="Label this file" className="flex-1 px-2 py-0.5 text-[11px] border border-[#E0EDE0] rounded focus:outline-none focus:border-[#2D8B57]" />
                      <select value={sf.type} onChange={e => { const up=[...suppFiles]; up[i]={...sf,type:e.target.value}; setSuppFiles(up); }}
                        className="px-2 py-0.5 text-[10px] border border-[#E0EDE0] rounded bg-white">
                        {['Raw Data','Extended Tables','Additional Figures','Appendix Data'].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <span className="text-[10px] text-[#888] shrink-0">{(sf.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => setSuppFiles(suppFiles.filter(x => x.id !== sf.id))} className="p-0.5 rounded hover:bg-red-50"><Trash2 size={11} className="text-[#C0392B]" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Key Findings (preserved) ─── */}
            <div className={`${cardCls} lg:col-span-2`}>
              <h4 className="text-[12px] font-bold text-[#1F4E2E] mb-2 flex items-center gap-1.5"><Lightbulb size={13} /> Key Findings</h4>
              <textarea value={findingsText} onChange={e => setFindingsText(e.target.value)} rows={4} placeholder="Write 3-5 bullet points:&#10;- Parent P3 had highest positive GCA&#10;- Cross P2×P3 was doubly recommended&#10;- Non-additive gene action predominant" className="w-full px-3 py-2 text-[12px] border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57] resize-none" data-testid="paper-findings" />
            </div>
          </div>
          <div className="flex justify-between mt-4">
            <button onClick={() => setStep(1)} className={btnSecondary}><ArrowLeft size={14} /> Back</button>
            <button onClick={() => { setStep(3); if (citations.length === 0) suggestCitations(); }} className={btnPrimary} data-testid="paper-next2">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Citation Intelligence ── */}
      {step === 3 && (
        <div data-testid="paper-step3">
          <h3 className={`${thCls} text-base mb-2`}>Step 3 — Citation Intelligence</h3>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <select value={citeStyle} onChange={e => setCiteStyle(e.target.value)} className="px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg bg-white focus:outline-none focus:border-[#2D8B57]" data-testid="paper-cite-style">
              {CITE_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={suggestCitations} disabled={citLoading} className={btnSecondary} data-testid="paper-refresh-cit">
              {citLoading ? <span className="animate-spin w-3 h-3 border-2 border-[#2D8B57] border-t-transparent rounded-full" /> : <Sparkles size={13} />}
              {citLoading ? 'Suggesting...' : 'Refresh Citations'}
            </button>
            <span className="text-[11px] text-[#888]">{citations.filter(c => c.included).length} of {citations.length} included</span>
          </div>
          {citLoading && citations.length === 0 && (
            <div className="text-center py-8 text-[#888] text-sm">Generating relevant citations for {setup.crop} / {setup.topic}...</div>
          )}
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {citations.map((c, i) => (
              <div key={i} className={`${cardCls} flex items-start gap-3 ${c.included ? 'border-[#2D8B57]/30' : 'opacity-50 border-dashed'}`} data-testid={`citation-${i}`}>
                <button onClick={() => { const up = [...citations]; up[i] = { ...c, included: !c.included }; setCitations(up); }}
                  className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center mt-0.5 transition ${c.included ? 'bg-[#2D8B57] border-[#2D8B57]' : 'border-[#ccc]'}`}>
                  {c.included && <Check size={12} className="text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[#333] leading-snug">{c.title}</p>
                  <p className="text-[11px] text-[#888] mt-0.5">{c.authors} ({c.year}) — <em>{c.journal}</em>{c.volume ? `, ${c.volume}` : ''}{c.pages ? `: ${c.pages}` : ''}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <label className="text-[12px] font-semibold text-[#1F4E2E] mb-1 block">Add DOI Links (optional)</label>
            <input type="text" value={extraDoi} onChange={e => setExtraDoi(e.target.value)} placeholder="Paste DOI links separated by commas" className="w-full px-3 py-2 text-sm border border-[#E0EDE0] rounded-lg focus:outline-none focus:border-[#2D8B57]" data-testid="paper-doi" />
          </div>
          <div className="flex justify-between mt-4">
            <button onClick={() => setStep(2)} className={btnSecondary}><ArrowLeft size={14} /> Back</button>
            <button onClick={() => setStep(4)} className={btnPrimary} data-testid="paper-next3">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* ── STEP 4: Generate Complete Paper ── */}
      {step === 4 && (
        <div data-testid="paper-step4">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className={`${thCls} text-base`}>Step 4 — Generate Complete Paper</h3>
            <button onClick={generateAll} disabled={Object.values(genLoading).some(Boolean)} className={btnPrimary} data-testid="paper-generate-all">
              <Sparkles size={14} /> Generate All Sections
            </button>
          </div>
          <div className="space-y-2">
            {PAPER_SECTIONS.map(s => {
              const content = sections[s.key];
              const isOpen = openPanel === s.key;
              const isLoading = genLoading[s.key];
              return (
                <div key={s.key} className={`${cardCls} ${content ? 'border-[#2D8B57]/30' : ''}`} data-testid={`section-${s.key}`}>
                  <div className="flex items-center justify-between">
                    <button onClick={() => setOpenPanel(isOpen ? null : s.key)} className="flex items-center gap-2 flex-1 text-left">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${content ? 'bg-[#2D8B57] text-white' : 'bg-[#eee] text-[#999]'}`}>{s.order}</span>
                      <span className="text-sm font-medium text-[#333]">{s.label}</span>
                      {content && <Check size={13} className="text-[#2D8B57]" />}
                      <ChevronDown size={14} className={`text-[#999] ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div className="flex items-center gap-1.5 ml-2">
                      {content && (
                        <button onClick={() => copyToClipboard(content, s.key)} className="p-1.5 rounded hover:bg-[#F4FAF5] transition" title="Copy">
                          {copyState === s.key ? <CheckCheck size={13} className="text-[#2D8B57]" /> : <Copy size={13} className="text-[#888]" />}
                        </button>
                      )}
                      <button onClick={() => generateSection(s.key)} disabled={isLoading} className="px-3 py-1 text-[11px] font-medium bg-[#F4FAF5] text-[#2D8B57] rounded-lg border border-[#D0E8D0] hover:bg-[#E8F5E9] disabled:opacity-50 transition flex items-center gap-1">
                        {isLoading ? <span className="animate-spin w-3 h-3 border-2 border-[#2D8B57] border-t-transparent rounded-full" /> : <Sparkles size={11} />}
                        {content ? 'Regenerate' : 'Generate'}
                      </button>
                    </div>
                  </div>
                  {isOpen && content && (
                    <div className="mt-3 pt-3 border-t border-[#F0F0F0]">
                      <p className="text-[13px] text-[#333] leading-relaxed whitespace-pre-line">{content}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quality Score */}
          {Object.keys(sections).length > 0 && (
            <div className={`${cardCls} mt-4`} data-testid="paper-quality">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-[#1F4E2E]">Paper Quality Score</h4>
                <span className={`text-lg font-bold ${qualityScore >= 70 ? 'text-[#2D8B57]' : qualityScore >= 40 ? 'text-[#E67E22]' : 'text-[#C0392B]'}`}>{qualityScore}/100</span>
              </div>
              <div className="w-full h-2.5 bg-[#eee] rounded-full overflow-hidden mb-3">
                <div className={`h-full rounded-full transition-all duration-500 ${qualityScore >= 70 ? 'bg-[#2D8B57]' : qualityScore >= 40 ? 'bg-[#E67E22]' : 'bg-[#C0392B]'}`} style={{ width: `${qualityScore}%` }} />
              </div>
              {qualityFlags.length > 0 && (
                <div className="space-y-1">
                  {qualityFlags.map((f, i) => (
                    <p key={i} className="text-[11px] text-[#C0392B] flex items-center gap-1.5"><AlertTriangle size={11} /> {f}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── AI Analysis Tools (Plagiarism, Journal Fit, Reviewer Sim, Hindi Abstract) ── */}
          {Object.keys(sections).length >= 2 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Plagiarism Checker */}
              <div className={cardCls} data-testid="paper-plagiarism">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[13px] font-bold text-[#1F4E2E] flex items-center gap-1.5"><Eye size={13} /> Originality Check</h4>
                  <button onClick={runPlagiarismCheck} disabled={plagLoading} className="px-3 py-1 text-[11px] font-medium bg-[#F4FAF5] text-[#2D8B57] rounded-lg border border-[#D0E8D0] hover:bg-[#E8F5E9] disabled:opacity-50 transition flex items-center gap-1">
                    {plagLoading ? <span className="animate-spin w-3 h-3 border-2 border-[#2D8B57] border-t-transparent rounded-full" /> : <Sparkles size={11} />} {plagLoading ? 'Checking...' : 'Run Check'}
                  </button>
                </div>
                {plagiarism && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-2xl font-bold ${plagiarism.score >= 80 ? 'text-[#2D8B57]' : plagiarism.score >= 50 ? 'text-[#E67E22]' : 'text-[#C0392B]'}`}>{plagiarism.score}/100</span>
                      <span className="text-[11px] text-[#888]">Originality</span>
                    </div>
                    <p className="text-[11px] text-[#555] mb-1">{plagiarism.summary}</p>
                    {(plagiarism.flags || []).map((f, i) => (
                      <p key={i} className="text-[10px] text-[#E67E22] flex items-center gap-1"><AlertTriangle size={9} />{f}</p>
                    ))}
                  </div>
                )}
                {!plagiarism && !plagLoading && <p className="text-[11px] text-[#aaa]">Analyzes text originality and flags template phrases</p>}
              </div>

              {/* Journal Fit Scorer */}
              <div className={cardCls} data-testid="paper-journal-fit">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[13px] font-bold text-[#1F4E2E] flex items-center gap-1.5"><Check size={13} /> Journal Fit Score</h4>
                  <button onClick={runJournalFit} disabled={jfLoading} className="px-3 py-1 text-[11px] font-medium bg-[#F4FAF5] text-[#2D8B57] rounded-lg border border-[#D0E8D0] hover:bg-[#E8F5E9] disabled:opacity-50 transition flex items-center gap-1">
                    {jfLoading ? <span className="animate-spin w-3 h-3 border-2 border-[#2D8B57] border-t-transparent rounded-full" /> : <Sparkles size={11} />} {jfLoading ? 'Scoring...' : 'Check Fit'}
                  </button>
                </div>
                {journalFit && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-2xl font-bold ${journalFit.score >= 7 ? 'text-[#2D8B57]' : journalFit.score >= 5 ? 'text-[#E67E22]' : 'text-[#C0392B]'}`}>{journalFit.score}/10</span>
                      <span className="text-[11px] text-[#888]">for {setup.journal}</span>
                    </div>
                    {(journalFit.strengths || []).map((s, i) => (
                      <p key={i} className="text-[10px] text-[#2D8B57] flex items-center gap-1"><Check size={9} />{s}</p>
                    ))}
                    {(journalFit.weaknesses || []).map((w, i) => (
                      <p key={i} className="text-[10px] text-[#C0392B] flex items-center gap-1"><AlertTriangle size={9} />{w}</p>
                    ))}
                    <p className="text-[11px] text-[#555] mt-1 italic">{journalFit.recommendation}</p>
                  </div>
                )}
                {!journalFit && !jfLoading && <p className="text-[11px] text-[#aaa]">Rates scope, methodology, and style match for {setup.journal || 'selected journal'}</p>}
              </div>

              {/* Reviewer Simulation */}
              <div className={`${cardCls} sm:col-span-2`} data-testid="paper-reviewer-sim">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[13px] font-bold text-[#1F4E2E] flex items-center gap-1.5"><MessageSquare size={13} /> Reviewer Simulation</h4>
                  <button onClick={runReviewerSim} disabled={revLoading} className="px-3 py-1 text-[11px] font-medium bg-[#F4FAF5] text-[#2D8B57] rounded-lg border border-[#D0E8D0] hover:bg-[#E8F5E9] disabled:opacity-50 transition flex items-center gap-1">
                    {revLoading ? <span className="animate-spin w-3 h-3 border-2 border-[#2D8B57] border-t-transparent rounded-full" /> : <Sparkles size={11} />} {revLoading ? 'Simulating...' : 'Simulate Reviews'}
                  </button>
                </div>
                {reviewerSim && (reviewerSim.reviews || []).map((r, i) => {
                  const sevColor = r.severity === 'Critical' ? '#C0392B' : r.severity === 'Major' ? '#E67E22' : '#2D8B57';
                  return (
                    <div key={i} className="border border-[#E0EDE0] rounded-lg p-3 mb-2 bg-[#FAFAFA]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded" style={{ background: sevColor }}>Reviewer {r.reviewer} — {r.severity}</span>
                      </div>
                      <p className="text-[12px] text-[#333] mb-1"><strong>Comment:</strong> {r.comment}</p>
                      <p className="text-[12px] text-[#2D8B57]"><strong>Suggested fix:</strong> {r.fix}</p>
                    </div>
                  );
                })}
                {!reviewerSim && !revLoading && <p className="text-[11px] text-[#aaa]">Predicts 3 likely reviewer comments with severity and suggested fixes</p>}
              </div>

              {/* Hindi Abstract */}
              <div className={`${cardCls} sm:col-span-2`} data-testid="paper-hindi-abstract">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[13px] font-bold text-[#1F4E2E] flex items-center gap-1.5"><FileText size={13} /> Hindi Abstract (for Indian university thesis)</h4>
                  <button onClick={generateHindiAbstract} disabled={hindiLoading} className="px-3 py-1 text-[11px] font-medium bg-[#F4FAF5] text-[#2D8B57] rounded-lg border border-[#D0E8D0] hover:bg-[#E8F5E9] disabled:opacity-50 transition flex items-center gap-1">
                    {hindiLoading ? <span className="animate-spin w-3 h-3 border-2 border-[#2D8B57] border-t-transparent rounded-full" /> : <Sparkles size={11} />} {hindiLoading ? 'Generating...' : 'Generate Hindi Abstract'}
                  </button>
                </div>
                {hindiAbstract && (
                  <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-lg p-3 relative">
                    <button onClick={() => copyToClipboard(hindiAbstract, 'hindi')} className="absolute top-2 right-2 p-1 rounded hover:bg-[#FFE082] transition">
                      {copyState === 'hindi' ? <CheckCheck size={12} className="text-[#2D8B57]" /> : <Copy size={12} className="text-[#888]" />}
                    </button>
                    <p className="text-[13px] text-[#333] leading-relaxed pr-6" style={{ fontFamily: 'Noto Sans Devanagari, sans-serif' }}>{hindiAbstract}</p>
                  </div>
                )}
                {!hindiAbstract && !hindiLoading && <p className="text-[11px] text-[#aaa]">Generates abstract in Devanagari Hindi for Indian university thesis submissions</p>}
              </div>
            </div>
          )}

          <div className="flex justify-between mt-4">
            <button onClick={() => setStep(3)} className={btnSecondary}><ArrowLeft size={14} /> Back</button>
            <button onClick={() => setStep(5)} disabled={Object.keys(sections).length === 0} className={btnPrimary} data-testid="paper-next4">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* ── STEP 5: Export ── */}
      {step === 5 && (
        <div data-testid="paper-step5">
          <h3 className={`${thCls} text-base mb-4`}>Step 5 — Export Options</h3>
          <div className={`${cardCls} mb-4`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-[#1F4E2E]">Paper Summary</h4>
              <span className={`text-sm font-bold ${qualityScore >= 70 ? 'text-[#2D8B57]' : 'text-[#E67E22]'}`}>Quality: {qualityScore}/100</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px] text-[#555]">
              <div><strong>Crop:</strong> {setup.crop}</div>
              <div><strong>Journal:</strong> {setup.journal}</div>
              <div><strong>Sections:</strong> {Object.keys(sections).length}/9</div>
              <div><strong>Citations:</strong> {citations.filter(c => c.included).length}</div>
              <div><strong>Figures:</strong> {figures.length}</div>
              <div><strong>Style:</strong> {citeStyle}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={exportDocx} disabled={exportLoading === 'docx'} className={`${cardCls} hover:border-[#2D8B57] transition text-left`} data-testid="paper-export-docx">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2D8B57]/10 flex items-center justify-center">{exportLoading === 'docx' ? <span className="animate-spin w-4 h-4 border-2 border-[#2D8B57] border-t-transparent rounded-full" /> : <Download size={18} className="text-[#2D8B57]" />}</div>
                <div>
                  <h4 className="text-sm font-bold text-[#1F4E2E]">Download Word Document</h4>
                  <p className="text-[11px] text-[#888]">TNR 12pt, double-spaced, figures embedded</p>
                </div>
              </div>
            </button>
            <button onClick={exportPdf} className={`${cardCls} hover:border-[#2D8B57] transition text-left`} data-testid="paper-export-pdf">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#C0392B]/10 flex items-center justify-center"><Download size={18} className="text-[#C0392B]" /></div>
                <div>
                  <h4 className="text-sm font-bold text-[#1F4E2E]">Download PDF</h4>
                  <p className="text-[11px] text-[#888]">Ready to submit</p>
                </div>
              </div>
            </button>
            <button onClick={() => copyToClipboard(sections.abstract || '', 'export-abs')} className={`${cardCls} hover:border-[#2D8B57] transition text-left`} data-testid="paper-copy-abstract">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2980B9]/10 flex items-center justify-center">{copyState === 'export-abs' ? <CheckCheck size={18} className="text-[#2980B9]" /> : <Copy size={18} className="text-[#2980B9]" />}</div>
                <div>
                  <h4 className="text-sm font-bold text-[#1F4E2E]">Copy Abstract Only</h4>
                  <p className="text-[11px] text-[#888]">For conference submissions</p>
                </div>
              </div>
            </button>
            <button onClick={() => copyToClipboard(sections.references || '', 'export-ref')} className={`${cardCls} hover:border-[#2D8B57] transition text-left`} data-testid="paper-copy-refs">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#8E44AD]/10 flex items-center justify-center">{copyState === 'export-ref' ? <CheckCheck size={18} className="text-[#8E44AD]" /> : <Copy size={18} className="text-[#8E44AD]" />}</div>
                <div>
                  <h4 className="text-sm font-bold text-[#1F4E2E]">Copy References Only</h4>
                  <p className="text-[11px] text-[#888]">Formatted in {citeStyle} style</p>
                </div>
              </div>
            </button>
          </div>
          <div className="flex justify-start mt-4">
            <button onClick={() => setStep(4)} className={btnSecondary}><ArrowLeft size={14} /> Back to Editor</button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════ */
const TABS = [
  { key: 'ai-assistant', label: 'AI Assistant', icon: MessageSquare, color: '#6B8E7B' },
  { key: 'choose-analysis', label: 'Choose Analysis', icon: HelpCircle, color: '#E67E22' },
  { key: 'interpretation', label: 'Interpretation Guides', icon: BookOpen, color: '#2980B9' },
  { key: 'write-results', label: 'Write My Results', icon: FileEdit, color: '#16A085' },
  { key: 'write-paper', label: 'Write My Paper', icon: FileText, color: '#1565C0' },
  { key: 'glossary', label: 'Statistical Glossary', icon: BookText, color: '#8E44AD' },
  { key: 'inspiration', label: 'Research Inspiration', icon: Sparkles, color: '#C0392B' },
];

export default function BKIntelligence() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ai-assistant');

  return (
    <div className="min-h-screen" style={{ background: 'var(--page-bg)' }} data-testid="bk-intelligence-page">
      <SharedNav />

      <div className="max-w-[1320px] mx-auto px-6 page-enter" style={{ paddingTop: 24 }}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-1 p-1.5 bg-white rounded-xl" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', width: 'max-content' }}>
              {TABS.map(t => {
                const Icon = t.icon;
                const isActive = activeTab === t.key;
                return (
                  <button key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className="flex items-center gap-1.5 font-body whitespace-nowrap btn-press transition-all"
                    style={{
                      padding: '8px 16px', borderRadius: 'var(--radius-md)',
                      fontSize: 13, fontWeight: 500,
                      background: isActive ? 'var(--green-900)' : 'transparent',
                      color: isActive ? 'white' : 'var(--text-medium)',
                    }}
                    data-testid={`bki-tab-${t.key}`}>
                    <Icon size={13} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab === 'ai-assistant' && (
            <div className="mt-4 rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', height: 'calc(100vh - 160px)' }}>
              <ResearchAssistant embedded />
            </div>
          )}

          {activeTab === 'choose-analysis' && (
            <div className="mt-4"><ChooseAnalysisWizard /></div>
          )}

          {activeTab === 'interpretation' && (
            <div className="mt-4"><InterpretationGuides /></div>
          )}

          {activeTab === 'write-results' && (
            <div className="mt-4"><WriteMyResults /></div>
          )}

          {activeTab === 'write-paper' && (
            <div className="mt-4"><WriteMyPaper /></div>
          )}

          {activeTab === 'glossary' && (
            <div className="mt-4"><StatisticalGlossary /></div>
          )}

          {activeTab === 'inspiration' && (
            <div className="mt-4"><ResearchInspiration /></div>
          )}
        </Tabs>
      </div>
    </div>
  );
}
